"use client";

import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { useRouter } from "next/navigation";

import { useAuth } from "@/app/lib/hooks/useAuth";
import { useSessions } from "@/app/lib/hooks/useSessions";
import { useProviders } from "@/app/lib/hooks/useProviders";
import { effectiveDefaultProviderIds } from "@/app/lib/providerOrder";
import { isFeatureEnabled, refreshClientConfig } from "@/app/lib/clientConfig";
import { refreshContent } from "@/app/lib/content";
import { usePreferences } from "@/app/lib/hooks/usePreferences";
import { useChat } from "@/app/lib/hooks/useChat";
import { useAdminBadges, BADGE_PILL_STYLE, formatBadgeCount } from "@/app/lib/hooks/useAdminBadges";

import Sidebar from "@/app/components/history/Sidebar";
import HistorySheet from "@/app/components/history/HistorySheet";
import NewFolderDialog from "@/app/components/common/NewFolderDialog";
import SettingsPanel from "@/app/components/settings/SettingsPanel";
import Toast from "@/app/components/common/Toast";

import BottomToolbar from "@/app/components/chat/BottomToolbar";
import PromptComposer from "@/app/components/chat/PromptComposer";
import ResponseGrid from "@/app/components/chat/ResponseGrid";
import ResponsePager from "@/app/components/chat/ResponsePager";
import JudgeCard from "@/app/components/chat/JudgeCard";
import ResponseCard from "@/app/components/chat/ResponseCard";
import DeepResearchCard from "@/app/components/chat/DeepResearchCard";
import DeepResearchDocument from "@/app/components/chat/DeepResearchDocument";
import CorpusDocumentViewer from "@/app/components/chat/CorpusDocumentViewer";
import DeepResearchProgress from "@/app/components/chat/DeepResearchProgress";
import CardExpandPopup from "@/app/components/chat/CardExpandPopup";
import TTSTransportBar from "@/app/components/chat/TTSTransportBar";
import { useTTSPlayer } from "@/app/lib/hooks/useTTSPlayer";
import { useTTSCacheStatus } from "@/app/lib/hooks/useTTSCacheStatus";

import Modal from "@/app/components/common/Modal";
import HelpModal from "@/app/components/help/HelpModal";
import FriendManager from "@/app/components/friends/FriendManager";
import SharePanel from "@/app/components/friends/SharePanel";
import ManageSharing from "@/app/components/friends/ManageSharing";
import { fetchFolderContext, fetchFolderMemory, deleteFolderMemoryItem, generateFolderContext, uploadAttachments, fetchCustomQueries, fetchSharedInbox, fetchFriends, createSession as apiCreateSessionDirect, searchSessions, reorderFolders as apiReorderFolders, updateSession as apiUpdateSession, batchSessions, summarizePrompt, getAuthHeaders, deleteJudgeVerdict, fetchMemories, fetchSession, patchRecord, classifyChat, deleteRecord, deleteMemory, drainScheduled, draftDeepResearchBrief, startDeepResearchBrief, pollDeepResearchBrief, startDeepResearch, pollDeepResearch, cancelDeepResearch, fetchActiveDeepResearch, fetchJudgeStatsExcludes, setJudgeStatsExclude } from "@/app/lib/api";
import {
  outboxDeleteSession,
  outboxLeaveSharedSession,
  installOutboxLifecycle,
} from "@/app/lib/outbox";
import { streamJudge } from "@/app/lib/sse";
import { getDeepResearchDisplayedToolCounts } from "@/app/lib/types";
import type { JudgeRequest, ProviderInfo, UserPreferences, ClassifyAttachment, ClassifyDecision, DeepResearchBriefResponse, DeepResearchJobStatus, DeepResearchSource } from "@/app/lib/types";
import { computeAttachmentStats } from "@/app/lib/attachmentStats";
import type { ProviderRequest, ChatRecord, CustomQueryResponse, SearchResponse, FolderMemoryItem } from "@/app/lib/types";
import { compareFolders } from "@/app/lib/types";
import { FALLBACK_MODES } from "@/app/lib/constants";
import { modeRank, resolveDefaultMode } from "@/app/lib/modes";
import { followUpProviderIDs } from "@/app/lib/followUpProviders";
import { hashPrompt } from "@/app/lib/promptHash";
import { buildSpokenJudgeText, judgeSpeechCardId } from "@/app/lib/judgeSpeech";
import { deepResearchSpokenText } from "@/app/lib/deepResearchSpeech";

// Sentinel key for per-session bookkeeping maps (judgingSessions,
// autoJudgePressedAtRef, etc.) when the chat is temp and has no real
// server session id. Only one temp chat is active at a time so a
// constant key is safe. Matches the server's inline-records judge path
// (server/main.py:2458) which accepts `session_id: null`.
const TEMP_JUDGE_KEY = "__temp__";
const DEEP_RESEARCH_TERMINAL_STATUSES = new Set<DeepResearchJobStatus["status"]>([
  "done",
  "cancelled",
  "failed",
  "error",
]);
const DEEP_RESEARCH_POLL_FAILURES_BEFORE_NOTICE = 3;
const TITLE_RETRY_FALLBACK_MS = 60_000;
const TITLE_RETRY_SCHEDULE_MS = [10_000, 30_000, TITLE_RETRY_FALLBACK_MS];

function folderMemoryTypeLabel(type: FolderMemoryItem["type"]): string {
  switch (type) {
    case "constraint": return "Constraint";
    case "preference": return "Preference";
    case "decision": return "Decision";
    case "entity": return "Option";
    case "open_thread": return "Open thread";
  }
}

/**
 * The send-to-judge safety net only applies after response generation ends.
 * A streaming turn, including a long background-job turn, has not yet reached
 * the record-save handoff that the safety net is meant to guard.
 */
export function isTurnLegitimatelyInProgress(isStreaming: boolean): boolean {
  return isStreaming;
}

function isInterruptedRecord(record: ChatRecord, isLatest: boolean, now = Date.now()): boolean {
  if (!isLatest || record.judgeVerdict) return false;
  const createdAt = record.timestamp.getTime();
  if (!Number.isFinite(createdAt) || now - createdAt <= 20 * 60 * 1000) return false;
  return Object.values(record.responses).every((response) => !response.text.trim());
}

function isDeepResearchTerminal(status: DeepResearchJobStatus["status"]): boolean {
  return DEEP_RESEARCH_TERMINAL_STATUSES.has(status);
}

function createDeepResearchClientRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

interface ActiveDeepResearch {
  jobId: string;
  sessionId: string;
  isTemporary: boolean;
  prompt: string;
  brief: string;
  fromFallback: boolean;
  expectedDurationSeconds?: number;
  startedAt: number;
  status: DeepResearchJobStatus;
  cancelling: boolean;
  attachmentsIgnored: boolean;
  startedInThisTab?: boolean;
}

interface DeepResearchDocumentData {
  report: string;
  sources: DeepResearchSource[];
  brief: string;
  fromFallback: boolean;
}

function deepResearchMetadata(record: ChatRecord): { brief: string; fromFallback: boolean; sources: DeepResearchSource[]; durationSeconds?: number; costUsd?: number; corpusDocumentsRead?: number; corpusDocumentsCited?: number; sourceCount?: number } {
  const metadata = record.responses[record.providerOrder[0]]?.metadata || {};
  const sources = Array.isArray(metadata.sources) ? metadata.sources.filter((source): source is DeepResearchSource =>
    !!source && typeof source === "object" && typeof (source as DeepResearchSource).url === "string"
  ) : [];
  const brief = typeof metadata.brief_text === "string" ? metadata.brief_text : typeof metadata.brief === "string" ? metadata.brief : record.prompt;
  const lines = brief.split("\n").map((line) => line.trim()).filter(Boolean);
  return {
    brief,
    // The server persists the brief itself as the record prompt. Its fallback
    // shape is the question followed by the identical single sub-question.
    fromFallback: metadata.from_fallback === true || (lines.length === 2 && lines[1] === `1. ${lines[0]}`),
    sources,
    durationSeconds: typeof metadata.duration_seconds === "number" ? metadata.duration_seconds : undefined,
    costUsd: typeof metadata.cost_usd === "number" ? metadata.cost_usd : undefined,
    // This exact server key is optional: zero is meaningful and must remain visible.
    corpusDocumentsRead: typeof metadata.corpus_documents_read === "number" ? metadata.corpus_documents_read : undefined,
    corpusDocumentsCited: typeof metadata.corpus_documents_cited === "number" ? metadata.corpus_documents_cited : undefined,
    sourceCount: Array.isArray(metadata.sources) ? metadata.sources.length : undefined,
  };
}

// =============================================================================
// Provider request builder — used for both manual modes and Auto-mode dispatch.
// Mirrors iOS ProviderOptionsBuilder.swift + the per-provider transforms in
// CLAUDE.md "Provider options (canonical pattern)". When `classifierParams`
// is provided (Auto mode), it overlays on top of registry defaults AFTER the
// canonical-to-wire transforms — classifier returns wire-format keys.
// =============================================================================

function buildProviderRequestFor(
  pid: string,
  mode: string,
  providers: ProviderInfo[],
  preferences: UserPreferences | null | undefined,
  classifierParams?: Record<string, unknown>
): import("@/app/lib/types").ProviderRequest | null {
  const prov = providers.find((p) => p.id === pid);
  if (!prov) return null;
  const modeModel = prov.models_by_mode[mode];
  if (!modeModel) return null;

  const options: Record<string, unknown> = {};
  // Auto mode delegates to the classifier — skip user param overrides so the
  // user's manual tuning doesn't fight the classifier's per-mode picks.
  const isAuto = !!classifierParams;

  // Step 1: registry defaults (+ user overrides for non-Auto).
  // Per-mode keys are strictly isolated — never fall back to another mode's
  // override or default. v1.2 §1B: Thinking-mode overrides bleeding into
  // Fast was hypothesized as a Gemini empty-stream trigger; audit confirmed
  // the read paths here and in SettingsPanel.tsx are already strict.
  for (const param of prov.configurable_parameters) {
    if (
      param.group_priority &&
      param.group_priority[mode] !== undefined &&
      param.group_priority[mode] < 0
    )
      continue;
    const userOverride = !isAuto
      ? preferences?.parameter_overrides?.[pid]?.[mode]?.[param.key]
      : undefined;
    if (userOverride !== undefined && userOverride !== null) {
      options[param.key] = userOverride;
    } else {
      const modeDefault = param.default_by_mode?.[mode];
      if (modeDefault !== undefined && modeDefault !== null) {
        options[param.key] = modeDefault;
      } else if (param.default !== undefined && param.default !== null) {
        options[param.key] = param.default;
      }
    }
  }

  // Step 2: per-provider canonical-to-wire transforms.
  if (pid === "anthropic") {
    if (options.thinking_budget !== undefined) {
      options.budget_tokens = options.thinking_budget;
      delete options.thinking_budget;
    }
    if (typeof window !== "undefined") {
      const w = window.innerWidth;
      const h = window.innerHeight;
      options.artifact_viewport_width = Math.min(w, h);
      options.artifact_landscape_width = Math.max(w, h);
    }
  } else if (pid === "gemini") {
    const genConfig: Record<string, unknown> = {};
    if (options.max_output_tokens !== undefined) {
      genConfig.maxOutputTokens = options.max_output_tokens;
      delete options.max_output_tokens;
    }
    if (options.thinking_level !== undefined) {
      genConfig.thinkingLevel = options.thinking_level;
      delete options.thinking_level;
    }
    if (Object.keys(genConfig).length > 0) {
      options.generationConfig = genConfig;
    }
    options.enable_tools = true;
    if (mode === "deep_research") {
      options.use_interactions_api = true;
    }
  } else if (pid === "openai") {
    options.enable_tools = true;
  }

  // Step 3: classifier overrides on top (already wire-format). For Gemini,
  // a nested generationConfig from the classifier is shallow-merged into
  // any existing generationConfig from registry defaults so we don't drop
  // either side's keys.
  if (classifierParams) {
    for (const [k, v] of Object.entries(classifierParams)) {
      if (
        pid === "gemini" &&
        k === "generationConfig" &&
        typeof v === "object" &&
        v != null
      ) {
        const existing = (options.generationConfig as Record<string, unknown>) || {};
        options.generationConfig = { ...existing, ...(v as Record<string, unknown>) };
      } else {
        options[k] = v;
      }
    }
  }

  return {
    provider: pid,
    model: modeModel.model,
    mode,
    ...(Object.keys(options).length > 0 ? { options } : {}),
  };
}

// =============================================================================
// Prompt Copy Button (shows checkmark after copy)
// =============================================================================

function PromptCopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="btn-ghost prompt-copy-btn"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}
      style={{ position: "absolute", top: "8px", right: "14px", padding: "4px", lineHeight: 0, opacity: 0.5 }}
      title="Copy prompt"
    >
      {copied ? (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13.25 4.75l-6.5 6.5-3.5-3.5" /></svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5.5" y="5.5" width="8" height="8" rx="1.5" /><path d="M10.5 5.5V3.5a1.5 1.5 0 00-1.5-1.5H3.5A1.5 1.5 0 002 3.5V9a1.5 1.5 0 001.5 1.5h2" /></svg>
      )}
    </button>
  );
}

// =============================================================================
// Main App Shell
// =============================================================================

export default function AppPage() {
  const router = useRouter();

  // --- Auth ---
  const { isAuthenticated, email, logout, isLoading: authLoading, helpViewCount, isAdmin } = useAuth();

  // Global admin-badge poller — drives the Settings gear badge in the header.
  // SettingsPanel runs its own instance to own markTabRead; two 30-s polls is
  // fine and keeps state local to each consumer.
  const { counts: adminBadgeCounts } = useAdminBadges(true, isAdmin);

  // --- Core data hooks ---
  const {
    sessions,
    folders,
    activeSessionId,
    isLoading: sessionsLoading,
    setActiveSession,
    createSession,
    deleteSession,
    updateSession,
    loadSessions,
    loadFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    getSession,
  } = useSessions();

  const {
    providers,
    modes,
    selectedProviders,
    toggleProvider,
    setSelectedProviders,
    isLoading: providersLoading,
  } = useProviders();

  const { preferences, loadPreferences } = usePreferences();

  const {
    chatHistory,
    activeResponses,
    activeProviderOrder,
    isStreaming,
    isLoadingHistory,
    sendMessage,
    stopStreaming,
    recentlyStoppedRecord,
    stopJustPressed,
    discardRecentlyStopped,
    loadHistory,
    clearChat,
    retryProvider,
    setRecordJudgeVerdict,
    removeRecord,
    restoreRecord,
    chatHistoryRef,
    activeRecordId,
  } = useChat(
    activeSessionId,
    useCallback((restorePrompt: string | null) => {
      if (restorePrompt) {
        // Returning to a session with an active stream — restore the prompt
        setPendingPrompt(restorePrompt);
        pendingPromptTime.current = new Date();
      } else {
        // Switching away — clear prompt state
        setPendingPrompt(null);
        setPendingAttachments([]);
        setSentAttachments([]);
      }
    }, []),
    // Pending-discard path: useChat's drain watcher fires the queued
    // record-delete after recordId lands. The server cascades the
    // session-delete in the same atomic write when this was the last
    // record (see server/storage.py:delete_record). This callback runs
    // when useChat sees session_deleted: true in the response — we
    // mirror locally via deleteSession (tombstone + filter) and then
    // force a loadSessions refresh so the sidebar reflects server
    // truth instantly instead of waiting for the next 5s poll.
    useCallback((sid: string) => {
      void (async () => {
        try {
          await deleteSession(sid);
        } catch (err) {
          console.error("Failed to delete emptied session after discard:", err);
        }
        void loadSessions();
      })();
    }, [deleteSession, loadSessions]),
    modes
  );

  // --- TTS read-aloud player ---
  // Drives the floating transport bar + per-card "Read aloud" buttons.
  // Single global state for the whole chat — one card plays at a time
  // (cross-tab coordination via BroadcastChannel happens inside the hook).
  // Bump after an async config refresh so feature-gated controls reflect a
  // newly fetched kill switch without making any config read asynchronous.
  const [, setClientConfigRevision] = useState(0);
  const ttsPlayer = useTTSPlayer();
  const ttsEnabled = isFeatureEnabled("tts");
  const autoJudgeEnabled = isFeatureEnabled("auto_judge");
  // Per-card cache-status tracker for the hollow-vs-solid play-button
  // indicator. Tells the user at a glance whether tapping play will be
  // instant + free (cached) or will cost OpenAI tokens to synthesize.
  const ttsCacheStatus = useTTSCacheStatus();
  const handleReadAloud = useCallback(
    (rid: string, providerId: string, text: string) => {
      if (!isFeatureEnabled("tts")) return;
      void ttsPlayer.play({ recordId: rid, providerId, text });
    },
    [ttsPlayer],
  );
  const ttsProgress =
    ttsPlayer.duration > 0 ? Math.min(1, ttsPlayer.currentTime / ttsPlayer.duration) : 0;
  // True when the given (recordId, providerId) is the active TTS target,
  // regardless of state (fetching / playing / paused). Used to drive the
  // border-glow + progress bar on the active card.
  const isCardTTSActive = useCallback(
    (rid: string | undefined, providerId: string) =>
      !!rid &&
      !!ttsPlayer.active &&
      ttsPlayer.active.recordId === rid &&
      ttsPlayer.active.providerId === providerId,
    [ttsPlayer.active],
  );
  // Helper that returns true ONLY when the per-(record, provider) cache
  // probe came back hit. Unknown/miss → returns false → hollow icon.
  // Defined here (not inline at every card site) so the iconography
  // logic is single-source.
  const isCardTTSCached = useCallback(
    (rid: string | undefined, providerId: string) =>
      !!rid && ttsCacheStatus.status(rid, providerId) === "hit",
    [ttsCacheStatus],
  );
  useEffect(() => {
    if (!ttsEnabled && ttsPlayer.active) ttsPlayer.stop();
  }, [ttsEnabled, ttsPlayer.active, ttsPlayer.stop]);

  // --- Local UI state ---
  const [selectedMode, setSelectedMode] = useState("auto");
  const [activeDeepResearch, setActiveDeepResearch] = useState<ActiveDeepResearch | null>(null);
  const activeDeepResearchRef = useRef<ActiveDeepResearch | null>(null);
  const deepResearchPollTimerRef = useRef<number | null>(null);
  const deepResearchPollingJobRef = useRef<string | null>(null);
  const deepResearchActiveFetchRef = useRef(false);
  useEffect(() => {
    activeDeepResearchRef.current = activeDeepResearch;
  }, [activeDeepResearch]);
  const [isPreparingDeepResearch, setIsPreparingDeepResearch] = useState(false);
  const [deepResearchDocument, setDeepResearchDocument] = useState<DeepResearchDocumentData | null>(null);
  const [corpusDocumentSource, setCorpusDocumentSource] = useState<DeepResearchSource | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<string | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [folderContextModal, setFolderContextModal] = useState<{ folderId: string; title: string; items: FolderMemoryItem[] } | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
    action?: { label: string; onClick: () => void };
  } | null>(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
  const pendingDeletesRef = useRef<Map<string, { timeoutId: ReturnType<typeof setTimeout>; kind: "session" | "folder" }>>(new Map());
  // Per-record soft-delete state for the long-press / right-click → Delete
  // query flow. `recordPendingDelete` drives the undo banner and is
  // cleared by commit (5s timer / send / visibility=hidden) or undo.
  // The timer + sessionId/index are stashed on a ref so commit handlers
  // can fire the server DELETE without re-running this component to
  // close over the latest state.
  const [recordPendingDelete, setRecordPendingDelete] = useState<{
    sessionId: string;
    recordId: string;
    promptPreview: string;
    record: ChatRecord;
    recordIndex: number;
  } | null>(null);
  const recordPendingDeleteRef = useRef<typeof recordPendingDelete>(null);
  const recordPendingDeleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Right-click / long-press context menu for the prompt bubble. Anchored
  // by the click coordinates; the menu's "Delete query" item triggers
  // beginRecordDelete and closes the menu. Click-outside closes too.
  const [promptContextMenu, setPromptContextMenu] = useState<{
    x: number;
    y: number;
    sessionId: string;
    recordId: string;
  } | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const [sentAttachments, setSentAttachments] = useState<{ name: string; id?: string }[]>([]);
  const [customQueries, setCustomQueries] = useState<CustomQueryResponse[]>([]);
  const [activeCustomQueryName, setActiveCustomQueryName] = useState<string | null>(null);
  const [activeCustomQueryPrompt, setActiveCustomQueryPrompt] = useState<string | null>(null);
  const [suggestedPrompt, setSuggestedPrompt] = useState<string | null>(null);
  const [isTempChat, setIsTempChat] = useState(false);
  const [tempChatFolderId, setTempChatFolderId] = useState<string | null>(null);
  const [newChatFolderId, setNewChatFolderId] = useState<string | null>(null);
  // Tracks sessions whose Summarize title generation has failed and is pending
  // retry. Key = session id. `prompt` is captured at first-attempt time so we
  // can retry without rebuilding the request. `firstFailureAt` is the epoch ms
  // of the first failure; if retries keep failing past 60s of sustained
  // failure, we fall back to truncation (INV-5, decision 5). Ref, not state —
  // retries fire from async contexts and we don't want to re-render.
  const pendingTitleRetriesRef = useRef<
    Map<string, { prompt: string; firstFailureAt: number }>
  >(new Map());
  const titleRetryTimerRef = useRef<number | null>(null);
  const titleRetryInFlightRef = useRef<Set<string>>(new Set());
  const titleRetryScheduleIndexRef = useRef<Map<string, number>>(new Map());
  // Desktop-web tap-to-expand a single output card into a 75vw popup with
  // the query above it. Click outside or ESC dismisses. Mobile no-op
  // (controls below gate on !isMobile). One of four discriminants:
  //   active-provider: streaming provider card in the active section
  //   active-synthesis: inflight auto-judge synthesis card
  //   history-provider: archived record's provider card
  //   history-judge: archived record's judge card
  const [expandedCard, setExpandedCard] = useState<
    | { kind: "active-provider"; providerId: string }
    | { kind: "active-synthesis" }
    | { kind: "history-provider"; recordIdx: number; providerId: string }
    | { kind: "history-judge"; recordIdx: number }
    | null
  >(null);

  const [showFriendManager, setShowFriendManager] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showManageSharing, setShowManageSharing] = useState(false);
  const [moveSessionId, setMoveSessionId] = useState<string | null>(null);
  // Single-session "Move → New Folder…": holds the session id while the
  // name dialog is open (moveSessionId is cleared so the pickers don't overlap).
  const [newFolderForSession, setNewFolderForSession] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [unseenSharedCount, setUnseenSharedCount] = useState(0);
  const [pendingFriendCount, setPendingFriendCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [composerPrefill, setComposerPrefill] = useState<{ text: string; id: number } | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showHelpCallout, setShowHelpCallout] = useState(false);
  // Session IDs currently being judged. A Set (not a single boolean) so a
  // user can kick off Judge on session A, navigate to session B, kick off
  // Judge there, and both run server-side in parallel with their own
  // loading states. The only blocker is re-entry on the *same* session.
  const [judgingSessions, setJudgingSessions] = useState<Set<string>>(new Set());
  // Scoring provider rolled per-session for the in-flight judge run. Used by
  // JudgeCard to color the "Judging…" header with the rotating judge's
  // brand color. Cleared when the call settles.
  const [judgingScoringProviders, setJudgingScoringProviders] = useState<Map<string, string>>(new Map());
  // Sessions where the user pressed the composer Judge button (auto-judge).
  // Drives the layout split: synthesis-card-leftmost in 4-up vs. post-hoc
  // footer below the 3 provider cards. Cleared when the judge run settles.
  // For brand-new-chat auto-judge, the session id is added the instant the
  // session is created (via handleSendMessage's onSessionCreated callback)
  // so the active-streaming render sees autoJudgeSessions.has(sid_B) from
  // the moment the new chat surfaces — no global "pending" flag needed.
  const [autoJudgeSessions, setAutoJudgeSessions] = useState<Set<string>>(new Set());
  // Captured Date.now() at composer-Judge / regenerate click. Used to compute
  // `total_duration_ms` (composer-tap → verdict landed) at the verdict event,
  // since the server doesn't know when the user clicked. iOS canonical:
  // ChatViewModel.swift `autoJudgePressedAt`. Per-session keyed.
  const autoJudgePressedAtRef = useRef<Map<string, number>>(new Map());

  // S5 — ResponsePager page state-carry across active→historical archive.
  // Keyed by record.serverRecordId for historical, plus the sentinel
  // "__active__" for the in-flight pager. Ref-backed (not state) so per-
  // swipe updates don't re-render the whole page tree; we trigger render
  // only at the archive transition where state actually matters.
  const pagerPagesRef = useRef<Map<string, number>>(new Map());
  // Bumped on archive transition to force a refresh that re-reads
  // initialPage for the now-historical pager.
  const [pagerEpoch, setPagerEpoch] = useState(0);

  const chatAreaRef = useRef<HTMLDivElement>(null);
  const bottomBarRef = useRef<HTMLDivElement>(null);
  const queryAreaRef = useRef<HTMLDivElement>(null);
  const defaultsApplied = useRef(false);
  const scrollToTopOnLoad = useRef(false);
  const pendingPromptTime = useRef<Date | null>(null);
  const hasScrolledForStream = useRef(false);
  // Memory toast: snapshot IDs before a query, compare after completion.
  const memoryIdsBeforeQuery = useRef<Set<string>>(new Set());
  // Memory toast carries the new memory IDs so the user can hit "Cancel"
  // within the 5-second display window to delete them. Without the IDs we
  // could only show the text; with them we can call DELETE /memories/{id}
  // for each freshly-created memory.
  const [memoryToast, setMemoryToast] = useState<{ message: string; ids: string[] } | null>(null);
  const wasStreaming = useRef(false);
  // Mirrors activeSessionId in a ref so async callbacks (like the judge
  // handler) can check "is the user still viewing the session I was
  // launched against?" without capturing stale state.
  const viewedSessionRef = useRef<string | null>(null);
  useEffect(() => {
    viewedSessionRef.current = activeSessionId;
  }, [activeSessionId]);

  // --- Outbox lifecycle: drain queued share / delete ops on connectivity
  // recovery, app foreground, and module load. Idempotent — runs once. ---
  useEffect(() => {
    installOutboxLifecycle();
  }, []);

  // --- Server-driven client config (v1.6): refresh on mount + on tab
  // foreground. Best-effort; getClientConfig() always returns cache-or-default
  // so nothing blocks on this. Lets us tune behavior / kill-switch features
  // server-side without a redeploy. ---
  useEffect(() => {
    const refresh = () => {
      void refreshClientConfig().then((updated) => {
        if (updated) setClientConfigRevision((revision) => revision + 1);
      });
    };
    refresh();
    void refreshContent();
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refresh();
        void refreshContent();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // --- Scheduled custom queries: drain any headless server-side runs into
  // history on app open + tab foreground. Fire-and-forget; the endpoint is
  // cheap to call unconditionally (returns { created: [] } when nothing is
  // pending). When it returns created sessions, refresh the session list so
  // the new scheduled-result sessions appear. Gated on auth (the endpoint is
  // per-user). ---
  useEffect(() => {
    if (!isAuthenticated) return;
    const drain = async () => {
      try {
        const { created } = await drainScheduled();
        if (created && created.length > 0) {
          await loadSessions();
        }
      } catch {
        // Best-effort; a failed drain just means scheduled results surface on
        // the next open/foreground.
      }
    };
    void drain();
    const onVisible = () => {
      if (document.visibilityState === "visible") void drain();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isAuthenticated, loadSessions]);

  // --- S5 archive transition: when a record archives (active →
  // historical), copy the active pager's current page index onto
  // the new historical record so it re-mounts on the same page the
  // user was viewing. Without this, a user who swiped to the
  // synthesis card during streaming jumps back to provider 0 the
  // moment streaming completes. ---
  const prevChatHistoryLenRef = useRef<number>(0);
  useEffect(() => {
    const prev = prevChatHistoryLenRef.current;
    const next = chatHistory.length;
    if (next > prev) {
      // One or more records just appended. For each newly-historical
      // record that has a serverRecordId, seed its pager page from
      // "__active__" if it isn't already set. (The common case is
      // a single new record; this loop handles the rare batch case
      // safely.)
      const activePage = pagerPagesRef.current.get("__active__");
      if (typeof activePage === "number") {
        for (let i = prev; i < next; i++) {
          const rec = chatHistory[i];
          if (rec?.serverRecordId && !pagerPagesRef.current.has(rec.serverRecordId)) {
            pagerPagesRef.current.set(rec.serverRecordId, activePage);
          }
        }
        // Reset the active sentinel — the next stream starts on page 0.
        pagerPagesRef.current.delete("__active__");
        // No pagerEpoch bump: the most-recent record's initialPage prop
        // reads "__active__" as a fallback on first render (see ResponsePager
        // below), so the pager is already on the correct page. Bumping epoch
        // would force a remount that visually pops the user back to page 0
        // for a frame before snapping to the carried page.
      }
    }
    prevChatHistoryLenRef.current = next;
  }, [chatHistory]);

  // --- TTS cache-status probe: observe completed (record, provider)
  //     pairs in the visible chat history so each card's play-button
  //     icon reflects whether its audio is cached (solid) or not
  //     (hollow). Idempotent — useTTSCacheStatus skips already-probed
  //     targets. Judge-card targets use iOS's winner-specific card id.
  //
  //     MOBILE ENABLED 2026-10-07 (owner decision). Safari may pause playback
  //     while backgrounded; the cache probe still runs so mobile card icons
  //     reflect their real cache status. ---
  useEffect(() => {
    if (!ttsEnabled) return;
    const targets: Array<{ recordId: string; providerId: string; text: string }> = [];
    for (const record of chatHistory) {
      if (!record.serverRecordId) continue;
      for (const [providerId, resp] of Object.entries(record.responses)) {
        if (resp.text && resp.isComplete) {
          if (record.mode === "deep_research") {
            const deepResearchProviderId = record.providerOrder[0];
            if (providerId === deepResearchProviderId) targets.push({ recordId: record.serverRecordId, providerId: deepResearchProviderId, text: deepResearchSpokenText(resp.text) });
          } else {
            targets.push({ recordId: record.serverRecordId, providerId, text: resp.text });
          }
        }
      }
      if (record.judgeVerdict?.synthesis) {
        targets.push({
          recordId: record.serverRecordId,
          providerId: judgeSpeechCardId(record.judgeVerdict),
          text: buildSpokenJudgeText(
            record.judgeVerdict,
            (id) => providers.find((provider) => provider.id === id)?.display_name,
          ),
        });
      }
    }
    if (targets.length > 0) ttsCacheStatus.observe(targets);
  }, [chatHistory, providers, ttsCacheStatus, ttsEnabled]);

  // --- After a TTS playback completes naturally (state → idle while
  //     we had an active target), mark that target as cached so the
  //     play button flips solid without waiting for the next probe.
  //     The server just wrote the bytes via /tts/stream-text's cache-
  //     write path (or the bytes came from cache to begin with). ---
  const ttsPrevStateRef = useRef(ttsPlayer.state);
  const ttsLastActiveRef = useRef(ttsPlayer.active);
  useEffect(() => {
    if (!ttsEnabled) {
      ttsPrevStateRef.current = ttsPlayer.state;
      return;
    }
    // Track the most recent non-null active target so we can attribute
    // a state transition to it even after `active` flips back to null.
    if (ttsPlayer.active) ttsLastActiveRef.current = ttsPlayer.active;
    const prev = ttsPrevStateRef.current;
    const curr = ttsPlayer.state;
    if ((prev === "playing" || prev === "paused") && curr === "idle") {
      const lastActive = ttsLastActiveRef.current;
      if (lastActive) {
        ttsCacheStatus.markHit(lastActive.recordId, lastActive.providerId);
      }
    }
    ttsPrevStateRef.current = curr;
  }, [ttsPlayer.state, ttsPlayer.active, ttsCacheStatus, ttsEnabled]);

  // --- Redirect if not authenticated ---
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      router.replace("/login");
    }
  }, [authLoading, isAuthenticated, router]);

  // --- Apply defaults from preferences (once) ---
  useEffect(() => {
    if (!preferences || providersLoading || defaultsApplied.current) return;
    setSelectedMode(resolveDefaultMode(preferences.default_mode, modes));
    setSelectedProviders(effectiveDefaultProviderIds(
      providers.map((provider) => provider.id),
      preferences.default_providers,
      preferences.provider_order,
    ));
    defaultsApplied.current = true;
  }, [preferences, modes, providers, providersLoading, setSelectedProviders]);

  // A preference can load before the provider registry. Once it arrives,
  // replace only the still-unmodified stored default if it is now retired.
  useEffect(() => {
    if (!preferences || modes.length === 0 || selectedMode !== preferences.default_mode) return;
    setSelectedMode(resolveDefaultMode(preferences.default_mode, modes));
  }, [preferences, modes, selectedMode]);

  // --- Set default providers once loaded (if prefs didn't set them) ---
  useEffect(() => {
    if (selectedProviders.length === 0 && providers.length > 0) {
      setSelectedProviders(providers.map((p) => p.id));
    }
  }, [providers, selectedProviders.length, setSelectedProviders]);

  // --- Favorite-provider one-shot send (the "A" button) ---
  // v1.6 Batch 7 (revised 2026-07-03): the "A" is NOT a mode toggle. It's a
  // second, always-lit send button that fires ONE query to the favorite
  // provider only (the first selected provider). No persistent single-provider
  // state — the next submit is multi-provider judge again. The abbrev/color
  // identify which provider the "A" targets.
  const enabledProvidersList = providers.filter((p) => p.enabled);
  const composerEffectiveProviders = followUpProviderIDs(
    selectedProviders,
    enabledProvidersList.map((p) => p.id),
    chatHistory.some((record) => record.mode === "deep_research"),
    selectedMode === "deep_research"
  );
  const firstSelectedProviderInfo =
    enabledProvidersList.find((p) => p.id === selectedProviders[0]) || enabledProvidersList[0];
  const singleProviderAbbrev = firstSelectedProviderInfo
    ? { anthropic: "A", gemini: "G", openai: "O" }[firstSelectedProviderInfo.id] ||
      firstSelectedProviderInfo.id.charAt(0).toUpperCase()
    : "?";
  const singleProviderColor = firstSelectedProviderInfo?.color;

  // --- Show help callout for first 5 visits, auto-dismiss after 6s ---
  // Gate on `isAuthenticated` rather than `helpViewCount > 0` so a fresh
  // signup (server returns `help_view_count = 0` on /auth/register and
  // only bumps on subsequent /auth/login + /auth/refresh — see
  // server/main.py:1711/1739) still triggers the callout the very first
  // time the chat view loads. The earlier `> 0` guard was meant to
  // suppress firing in the logged-out default state, but it also
  // collaterally suppressed the legitimate first-signup case (count=0,
  // user is authenticated). Mirrors iOS commit 02941d6 — same user-
  // visible fix, different code-level shape (iOS uses a 999 sentinel
  // for "not yet hydrated"; web uses `isAuthenticated`).
  useEffect(() => {
    if (isAuthenticated && helpViewCount <= 5) {
      setShowHelpCallout(true);
      const t = setTimeout(() => setShowHelpCallout(false), 6000);
      return () => clearTimeout(t);
    }
  }, [helpViewCount, isAuthenticated]);

  // --- Deep link: pick up session ID from /chat?id=... redirect ---
  useEffect(() => {
    const deepLinkSession = sessionStorage.getItem("trio_deep_link_session");
    if (deepLinkSession) {
      sessionStorage.removeItem("trio_deep_link_session");
      setActiveSession(deepLinkSession);
    }
  }, [setActiveSession]);

  // --- Responsive detection ---
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 900);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // --- Load custom queries + poll every 30s ---
  // Polling picks up edits made on the iOS client (or another web tab) so
  // the list stays in sync without a hard refresh. 30s is a deliberate
  // slowdown vs. friends/inbox (5s) because the queries list changes
  // much less frequently and the payload is larger per entry.
  // Callable refresh — used by the 30s polling effect below AND by
  // SettingsPanel's onCustomQueriesChanged callback so a create / edit /
  // delete / reorder reflects in the BottomToolbar's custom-query menu
  // without waiting for the next polling tick.
  const refreshCustomQueries = useCallback(() => {
    fetchCustomQueries()
      .then(setCustomQueries)
      .catch((err) => console.error("Failed to fetch custom queries:", err));
  }, []);
  useEffect(() => {
    refreshCustomQueries();
    const interval = setInterval(refreshCustomQueries, 30000);
    return () => clearInterval(interval);
  }, [refreshCustomQueries]);

  // --- Poll friends + shared inbox every 5 seconds ---
  useEffect(() => {
    const poll = () => {
      fetchFriends()
        .then((data) => setPendingFriendCount(data.pending_received.length))
        .catch((err) => console.error("Failed to poll friends:", err));
      fetchSharedInbox()
        .then((data) => setUnseenSharedCount(data.total_unseen))
        .catch((err) => console.error("Failed to poll shared inbox:", err));
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, []);

  // --- Auto-refresh the open session while its latest turn is in flight ---
  // After a hard reload (in-memory stream state wiped) or for a server-owned
  // judge (this client never fired /judge, so the judge-recovery poll never
  // runs), the open view would otherwise never re-fetch — leaving provider
  // cards blank or the judge card stuck on its pending spinner until a manual
  // navigation forces a reload. Poll loadHistory until every provider is
  // complete AND any expected verdict has landed, then stop. Skipped while
  // locally streaming (the live SSE path updates directly) and while the
  // client's own judge-recovery poll already owns this session.
  useEffect(() => {
    if (!activeSessionId) return;
    if (Object.keys(activeResponses).length > 0) return; // live SSE path covers it
    if (judgingSessions.has(activeSessionId)) return;     // recovery poll owns it
    const latest = chatHistory.length > 0 ? chatHistory[chatHistory.length - 1] : null;
    if (!latest) return;
    if (isInterruptedRecord(latest, true)) return;
    const providersPending = latest.providerOrder.some((pid) => {
      const r = latest.responses[pid];
      return !r || !r.isComplete;
    });
    const judgePending = latest.autoJudgeRequested === true && !latest.judgeVerdict;
    if (!providersPending && !judgePending) return;       // nothing in flight
    const interval = setInterval(() => { void loadHistory(activeSessionId); }, 5000);
    return () => clearInterval(interval);
  }, [activeSessionId, chatHistory, activeResponses, judgingSessions, loadHistory]);

  // --- Debounced search ---
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    const timeout = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchSessions(searchQuery.trim());
        setSearchResults(results);
      } catch (err) {
        console.error("Search failed:", err);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // --- Measure bottom bar and set CSS variable for dynamic card height ---
  useEffect(() => {
    const el = bottomBarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      document.documentElement.style.setProperty("--bottom-bar-h", `${el.offsetHeight}px`);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Track query area height for dynamic card sizing. When the pendingPrompt
  // area is unmounted (no active stream / no pending send), clear the
  // variable to 0 so card-height calcs that subtract `--query-area-h` don't
  // reserve phantom space for an absent prompt area.
  useEffect(() => {
    const el = queryAreaRef.current;
    if (!el) {
      document.documentElement.style.setProperty("--query-area-h", "0px");
      return;
    }
    const ro = new ResizeObserver(() => {
      document.documentElement.style.setProperty("--query-area-h", `${el.offsetHeight + 8}px`);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty("--query-area-h", "0px");
    };
  }, [chatHistory.length, pendingPrompt]);

  // Track attachment bar height as CSS variable (not via ResizeObserver — immediate)
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--attachment-bar-h",
      sentAttachments.length > 0 || pendingAttachments.length > 0 ? "30px" : "0px"
    );
  }, [sentAttachments.length, pendingAttachments.length]);

  // --- Auto-scroll: top on session load, bottom once when stream STARTS.
  // iOS canonical: ChatViewModel `scrollTrigger += 1` was removed (commit
  // ba7ca13) — completion no longer pulls the view to bottom. Web mirror:
  // do NOT depend on chatHistory or activeResponses. Listening to
  // activeResponses meant every streamed chunk + every provider/judge
  // completion fired this effect, snapping the page to bottom on each
  // event (the "shudder" the user reported). Now: scroll-to-top on
  // session change, scroll-to-bottom once at stream start, and that's it.
  // Reads chatHistory via ref to avoid making it a dep.
  const chatHistoryLenRef = useRef(0);
  useEffect(() => {
    chatHistoryLenRef.current = chatHistory.length;
  }, [chatHistory.length]);
  useEffect(() => {
    if (!chatAreaRef.current) return;
    if (scrollToTopOnLoad.current) {
      chatAreaRef.current.scrollTop = 0;
      scrollToTopOnLoad.current = false;
      return;
    }
    if (isStreaming) {
      if (!hasScrolledForStream.current) {
        hasScrolledForStream.current = true;
        // Brute-force scroll to the pending-prompt — bypassing
        // scroll-snap, layout-settle races, and React render timing.
        // History on why this got nuclear:
        //   1. scrollTop=scrollHeight (original) — failed because for
        //      plain Send the active cards are placeholder-empty at
        //      this moment, scrollHeight barely grows, scroll is
        //      visually a no-op.
        //   2. scrollIntoView({block:start}) on queryAreaRef — failed
        //      in user testing; suspect Safari's nearest-scrollable-
        //      ancestor walk picks the document instead of chat-area
        //      when the wrapper is `overflow: hidden` + flex.
        //   3. Single getBoundingClientRect-based scrollTop — failed
        //      in user testing; suspect scroll-snap (`scroll-snap-
        //      type: y proximity` on chat-area) snapping back to a
        //      historical record's snap-start, OR cards laying out
        //      AFTER our initial scroll fires, shifting the math out.
        //
        // What we do now: fire the scroll on rAF (after-paint) and
        // again at 100 ms and 400 ms (after card-mount-and-relayout).
        // Each attempt uses BOTH explicit scrollTop math AND scrollTo
        // — different code paths in different browsers. Console-log
        // tagged "[scroll]" so the actual scrollTop deltas are
        // visible in dev tools if this STILL doesn't work.
        // Last-pass investigation (2026-05-16T23:14 with user dev-tools
        // log) showed the scroll IS landing — chatEl.scrollTop went
        // 889 → 1018 successfully, no resnap. The reason it "looked
        // like nothing happened" was that the pendingPrompt box and
        // the prior turn's prompt box look near-identical (same
        // component, same timestamp + prompt-box layout), so the
        // user couldn't tell from a static snapshot that scroll
        // moved. Plus the user had dev-tools docked, shrinking the
        // chat-area's clientHeight to 135 px — a window that small
        // makes any scroll's visual delta hard to perceive.
        //
        // Fix: use smooth-scroll behavior so the motion is animated
        // and unmistakeable. Inline-disable scroll-snap during the
        // scroll as belt-and-suspenders (`scroll-snap-type: y
        // proximity` on the chat-area COULD interfere even with the
        // `.streaming` class theoretically setting it to `none` —
        // some browsers honor inline style faster than class change).
        const doScroll = () => {
          const chatEl = chatAreaRef.current;
          if (!chatEl) return;
          const queryEl = queryAreaRef.current;
          let target: number;
          if (chatHistoryLenRef.current === 0) {
            target = 0;
          } else if (queryEl) {
            const queryRect = queryEl.getBoundingClientRect();
            const chatRect = chatEl.getBoundingClientRect();
            target = chatEl.scrollTop + (queryRect.top - chatRect.top) - 8;
            target = Math.min(target, chatEl.scrollHeight - chatEl.clientHeight);
            target = Math.max(target, 0);
          } else {
            target = chatEl.scrollHeight;
          }
          const before = chatEl.scrollTop;
          // Force snap off inline — overrides the (slower-to-apply)
          // `.streaming` className change. Restored after the animation.
          const prevSnap = chatEl.style.scrollSnapType;
          chatEl.style.scrollSnapType = "none";
          chatEl.scrollTo({ top: target, behavior: "smooth" });
          // Restore snap behaviour 800 ms later — long enough for the
          // smooth-scroll animation to settle.
          setTimeout(() => {
            if (chatAreaRef.current) {
              chatAreaRef.current.style.scrollSnapType = prevSnap;
            }
          }, 800);
          // eslint-disable-next-line no-console
          console.log(`[scroll] follow-up scroll: before=${before} target=${target} clientH=${chatEl.clientHeight} scrollH=${chatEl.scrollHeight} queryEl=${queryEl ? "yes" : "no"}`);
        };
        requestAnimationFrame(doScroll);
        // Only one re-scroll, late enough for active cards to mount +
        // any CSS transitions to settle. Smooth scroll animations
        // don't stack well — multiple rapid invocations cancel each
        // other. Single re-attempt at 600 ms is enough; if the cards'
        // initial layout shifted the target, this catches it.
        setTimeout(doScroll, 600);
      }
    } else {
      hasScrolledForStream.current = false;
    }
  }, [activeSessionId, isStreaming]);

  // --- Auto-scroll on every new query start (Test 1.9 + pin-to-top parity) ---
  // The isStreaming-based scroll above only fires on the FIRST transition
  // false → true per session-load. A 2nd / 3rd query in the same session
  // can leave the user looking at the previous turn's output. Watch
  // pendingPrompt's null → set transition (which happens at the moment
  // a send dispatches) and re-fire the scroll so the new pending prompt
  // PINS to the top of the chat viewport, with the pending/streaming
  // provider cards rendered just beneath it — matching the requested iOS
  // behavior. Works for both the first query in a session and follow-ups.
  //
  // The clamp `Math.min(target, scrollHeight - clientHeight)` means the
  // query can only reach the very top when there's a full viewport of
  // content below it. The pending provider cards ARE ~full-viewport tall
  // (height: calc(100dvh - …)), but only AFTER they mount + lay out. So a
  // single early scroll often lands the query mid-viewport (cards not yet
  // tall enough to push it up). Fix: re-fire across several frames/timeouts
  // so once the cards have their real height we re-target and the query
  // settles at the top. Re-fires are cheap (a clamped scrollTo is a no-op
  // once we're already there) and don't fight the streaming auto-scroll —
  // that machinery only acts on stream-completion / judge-start, not here.
  const prevPendingPromptRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevPendingPromptRef.current;
    prevPendingPromptRef.current = pendingPrompt;
    if (prev !== null || pendingPrompt === null) return; // only on null → set
    const scroll = () => {
      const chatEl = chatAreaRef.current;
      const queryEl = queryAreaRef.current;
      if (!chatEl || !queryEl) return;
      const queryRect = queryEl.getBoundingClientRect();
      const chatRect = chatEl.getBoundingClientRect();
      // Pin the query's top to the top of the chat viewport (minus an 8px
      // breathing gap). Clamped to the max scrollable offset — once the
      // pending cards below have mounted there's enough height for this to
      // reach the true top.
      const target = Math.max(
        0,
        Math.min(
          chatEl.scrollTop + (queryRect.top - chatRect.top) - 8,
          chatEl.scrollHeight - chatEl.clientHeight,
        ),
      );
      const prevSnap = chatEl.style.scrollSnapType;
      chatEl.style.scrollSnapType = "none";
      chatEl.scrollTo({ top: target, behavior: "smooth" });
      setTimeout(() => {
        if (chatAreaRef.current) chatAreaRef.current.style.scrollSnapType = prevSnap;
      }, 800);
    };
    requestAnimationFrame(scroll);
    // Late re-fires — the streaming cards mount + grow to full height over
    // the next few hundred ms, shifting the scrollHeight (and thus the
    // clamp ceiling). Re-target a few times so the query reliably settles
    // pinned at the top once the cards are laid out, not stuck mid-viewport.
    const t1 = setTimeout(scroll, 120);
    const t2 = setTimeout(scroll, 350);
    const t3 = setTimeout(scroll, 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [pendingPrompt]);

  // --- Auto-scroll to judge card when judging starts (Test 1.9) ---
  // Watch judgingSessions for the current session newly appearing →
  // scroll the latest JudgeCard into the top of the visible area.
  const wasJudgingThisSessionRef = useRef(false);
  useEffect(() => {
    const sid = activeSessionId;
    const isJudgingNow = !!sid && judgingSessions.has(sid);
    const wasJudging = wasJudgingThisSessionRef.current;
    wasJudgingThisSessionRef.current = isJudgingNow;
    if (!isJudgingNow || wasJudging) return; // only on false → true
    const scroll = () => {
      const chatEl = chatAreaRef.current;
      if (!chatEl) return;
      // Find the latest JudgeCard in the DOM (rendered with data-judge-card).
      const judges = chatEl.querySelectorAll<HTMLElement>("[data-judge-card]");
      const last = judges[judges.length - 1];
      if (!last) return;
      const lastRect = last.getBoundingClientRect();
      const chatRect = chatEl.getBoundingClientRect();
      const target = Math.max(
        0,
        Math.min(
          chatEl.scrollTop + (lastRect.top - chatRect.top) - 8,
          chatEl.scrollHeight - chatEl.clientHeight,
        ),
      );
      const prevSnap = chatEl.style.scrollSnapType;
      chatEl.style.scrollSnapType = "none";
      chatEl.scrollTo({ top: target, behavior: "smooth" });
      setTimeout(() => {
        if (chatAreaRef.current) chatAreaRef.current.style.scrollSnapType = prevSnap;
      }, 800);
    };
    // Wait a frame so the JudgeCard has actually mounted in the DOM
    // before we measure its position.
    requestAnimationFrame(() => requestAnimationFrame(scroll));
    setTimeout(scroll, 400);
  }, [judgingSessions, activeSessionId]);

  // --- Stream-completion scroll preservation ---
  // When a stream finishes, useChat clears activeResponses (active pager
  // unmounts → chat-area content shrinks → browser clamps scrollTop down)
  // and a beat later loadHistory inflates chatHistory with the new record.
  // In between, the user's scroll position is lost — they "pop" away from
  // whatever they were looking at. Fix: track the user's scrollTop + a
  // "was at bottom" flag continuously during streaming, and restore once
  // chatHistory has the new record.
  const lastScrollTopRef = useRef(0);
  const wasAtBottomRef = useRef(true);
  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    const onScroll = () => {
      lastScrollTopRef.current = el.scrollTop;
      wasAtBottomRef.current =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const completionRestorePending = useRef(false);
  const prevIsStreamingForScrollRef = useRef(false);
  useEffect(() => {
    if (prevIsStreamingForScrollRef.current && !isStreaming) {
      // Stream just completed — flag for restore on next chatHistory update.
      completionRestorePending.current = true;
    }
    if (!prevIsStreamingForScrollRef.current && isStreaming) {
      // Stream just started — drop any stale flag (e.g. from a prior temp
      // completion where chatHistory never updated to consume it).
      completionRestorePending.current = false;
    }
    prevIsStreamingForScrollRef.current = isStreaming;
  }, [isStreaming]);
  useEffect(() => {
    if (completionRestorePending.current && chatAreaRef.current) {
      completionRestorePending.current = false;
      const wasAtBottom = wasAtBottomRef.current;
      const savedTop = lastScrollTopRef.current;
      requestAnimationFrame(() => {
        if (!chatAreaRef.current) return;
        if (wasAtBottom) {
          chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
        } else {
          chatAreaRef.current.scrollTop = savedTop;
        }
      });
    }
  }, [chatHistory]);
  // Switching sessions invalidates any pending restore — the saved scrollTop
  // belongs to the previous session's content.
  useEffect(() => {
    completionRestorePending.current = false;
    lastScrollTopRef.current = 0;
    wasAtBottomRef.current = true;
  }, [activeSessionId]);

  // --- Memory toast: detect new memories after a query completes ---
  useEffect(() => {
    if (isStreaming && !wasStreaming.current) {
      // Query just started — snapshot current memory IDs
      fetchMemories().then((resp) => {
        memoryIdsBeforeQuery.current = new Set(resp.memories.map((m: { id: string }) => m.id));
      }).catch(() => {});
    }
    if (!isStreaming && wasStreaming.current) {
      // Query just finished — wait for extraction, then check for new memories
      const timer = setTimeout(() => {
        fetchMemories().then((resp) => {
          const newMemories = resp.memories.filter(
            (m: { id: string; content: string }) => !memoryIdsBeforeQuery.current.has(m.id)
          );
          if (newMemories.length > 0) {
            const texts = newMemories.map((m: { content: string }) => m.content);
            const ids = newMemories.map((m: { id: string }) => m.id);
            setMemoryToast({
              message: "Memory saved: " + texts.join(" | "),
              ids,
            });
          }
        }).catch(() => {});
      }, 5000);
      wasStreaming.current = false;
      return () => clearTimeout(timer);
    }
    wasStreaming.current = isStreaming;
  }, [isStreaming]);

  // --- Handle session switch side effects (loading is handled by useChat) ---
  useEffect(() => {
    if (activeSessionId) {
      scrollToTopOnLoad.current = true;
    } else {
      clearChat();
      setSentAttachments([]);
    }
  }, [activeSessionId, clearChat]);

  // --- Sync sent attachments from last history record ---
  useEffect(() => {
    if (chatHistory.length > 0) {
      const lastRecord = chatHistory[chatHistory.length - 1];
      if (lastRecord.attachmentNames && lastRecord.attachmentNames.length > 0) {
        setSentAttachments(
          lastRecord.attachmentNames.map((name, i) => ({
            name,
            id: lastRecord.attachmentIds?.[i],
          }))
        );
      } else {
        setSentAttachments([]);
      }
    }
  }, [chatHistory]);

  // --- Derived session info (needed by handlers below) ---
  const activeSession = activeSessionId ? sessions.find((s) => s.id === activeSessionId) : undefined;
  // Resolve which folder owns the current composer.
  //   • Temp chat → tempChatFolderId.
  //   • Active session → that session's folder (or null if the session is
  //     unfiled — explicitly NOT falling back to newChatFolderId here. The
  //     fallback used to leak the "new chat in folder X" state onto an
  //     unfiled history item the user clicked into mid-setup, showing the
  //     wrong folder chip and risking placing follow-ups in folder X.)
  //   • No active session (i.e. fresh composer) → newChatFolderId, which
  //     is set by handleNewChatInFolder and cleared on send / new-chat /
  //     session-switch.
  const effectiveFolderId = isTempChat
    ? tempChatFolderId
    : activeSession
      ? activeSession.folder_id || null
      : newChatFolderId;
  const activeFolder = effectiveFolderId ? folders.find((f) => f.id === effectiveFolderId) : undefined;
  const activeFolderName = activeFolder?.name;
  const [folderContextDateCache, setFolderContextDateCache] = useState<string | null>(null);
  const [folderContextCache, setFolderContextCache] = useState<{ summary: string; versionId: string } | null>(null);
  const activeFolderContextDate = activeFolder ? (activeSession ? new Date(activeSession.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : (folderContextDateCache || undefined)) : undefined;

  // Fetch folder context when entering a new chat in a folder
  useEffect(() => {
    if (!effectiveFolderId || activeSession) { setFolderContextDateCache(null); setFolderContextCache(null); return; }
    fetchFolderContext(effectiveFolderId).then((ctx) => {
      if (ctx?.timestamp) {
        setFolderContextDateCache(new Date(ctx.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" }));
      }
      if (ctx?.summary && ctx?.version_id) {
        setFolderContextCache({ summary: ctx.summary, versionId: ctx.version_id });
      }
    }).catch(() => {});
  }, [effectiveFolderId, activeSession]);
  const activeSharedWith = activeSession?.shared && !activeSession.shared_by && activeSession.participants && activeSession.participants.length > 0 ? activeSession.participants : undefined;
  const activeSharedBy = activeSession?.shared_by || undefined;

  // --- Helpers ---

  const clearCustomQueryState = useCallback(() => {
    setActiveCustomQueryName(null);
    setActiveCustomQueryPrompt(null);
    setSuggestedPrompt(null);
  }, []);

  // Title retry window: once Summarize has been failing for this long we fall
  // back to a truncated first-prompt string so the sidebar doesn't show "New
  // Chat" forever (INV-5). Decision 5 calls for truncation only as an
  // absolute last resort.
  const queueTitleRetry = useCallback((sid: string, prompt: string) => {
    if (!pendingTitleRetriesRef.current.has(sid)) {
      pendingTitleRetriesRef.current.set(sid, { prompt, firstFailureAt: Date.now() });
      titleRetryScheduleIndexRef.current.set(sid, 0);
    }
  }, []);

  // Attempt Summarize + title PUT for a single pending-retry session. Returns
  // `true` if the entry was resolved (success OR truncation fallback) and can
  // be removed; `false` if it should stay queued for a future retry.
  const attemptTitleRetry = useCallback(
    async (
      sid: string,
      entry: { prompt: string; firstFailureAt: number }
    ): Promise<boolean> => {
      // The scheduled 60-second attempt must write the fallback immediately,
      // rather than waiting through one final summarize request.
      if (Date.now() - entry.firstFailureAt >= TITLE_RETRY_FALLBACK_MS) {
        const fallback = entry.prompt.slice(0, 60);
        try {
          await apiUpdateSession(sid, { title: fallback });
          loadSessions();
        } catch (putErr) {
          console.error("Title fallback PUT failed:", putErr);
        }
        return true;
      }
      try {
        const res = await summarizePrompt(entry.prompt);
        if (res.summary && res.summary !== "Untitled") {
          await apiUpdateSession(sid, { title: res.summary });
        }
        loadSessions();
        return true;
      } catch (err) {
        // Sustained-failure fallback: truncate after 60s.
        if (Date.now() - entry.firstFailureAt >= TITLE_RETRY_FALLBACK_MS) {
          const fallback = entry.prompt.slice(0, 60);
          try {
            await apiUpdateSession(sid, { title: fallback });
            loadSessions();
          } catch (putErr) {
            console.error("Title fallback PUT failed:", putErr);
          }
          console.error(
            `Title summarize retries exhausted for ${sid} after 60s; truncated.`,
            err
          );
          return true;
        }
        console.error(`Title summarize retry failed for ${sid}:`, err);
        return false;
      }
    },
    [loadSessions]
  );

  // Drain queued title retries. Called on (a) page visibility change back to
  // `visible`, (b) after a successful chat send. Per decision 5, retries run
  // until either Summarize succeeds or 60s has elapsed since the first
  // failure (then we truncate). No-op when the queue is empty.
  const drainTitleRetries = useCallback(async (sessionIds?: string[]) => {
    if (pendingTitleRetriesRef.current.size === 0) return;
    // Snapshot entries — the map may mutate during async work.
    const entries = sessionIds
      ? sessionIds.flatMap((sid) => {
          const entry = pendingTitleRetriesRef.current.get(sid);
          return entry ? [[sid, entry] as const] : [];
        })
      : Array.from(pendingTitleRetriesRef.current.entries());
    for (const [sid, entry] of entries) {
      if (titleRetryInFlightRef.current.has(sid)) continue;
      titleRetryInFlightRef.current.add(sid);
      try {
        const resolved = await attemptTitleRetry(sid, entry);
        if (resolved) {
          pendingTitleRetriesRef.current.delete(sid);
          titleRetryScheduleIndexRef.current.delete(sid);
        }
      } finally {
        titleRetryInFlightRef.current.delete(sid);
      }
    }
  }, [attemptTitleRetry]);

  // Retry failed titles on their own clock, including while a long-running
  // answer keeps the current send path occupied. The schedule is anchored to
  // the original failure: roughly 10, 30, and 60 seconds later.
  useEffect(() => {
    const drainScheduledTitleRetries = () => {
      const now = Date.now();
      const dueSessionIds: string[] = [];
      for (const [sid, entry] of pendingTitleRetriesRef.current) {
        if (titleRetryInFlightRef.current.has(sid)) continue;
        const scheduleIndex = titleRetryScheduleIndexRef.current.get(sid) ?? 0;
        const dueAt = TITLE_RETRY_SCHEDULE_MS[scheduleIndex];
        if (dueAt !== undefined && now - entry.firstFailureAt >= dueAt) {
          titleRetryScheduleIndexRef.current.set(sid, scheduleIndex + 1);
          dueSessionIds.push(sid);
        }
      }
      if (dueSessionIds.length > 0) void drainTitleRetries(dueSessionIds);
    };
    titleRetryTimerRef.current = window.setInterval(drainScheduledTitleRetries, 1_000);
    return () => {
      if (titleRetryTimerRef.current !== null) {
        window.clearInterval(titleRetryTimerRef.current);
        titleRetryTimerRef.current = null;
      }
    };
  }, [drainTitleRetries]);

  // Retry on page visibility change — per decision 5, when the user returns
  // the tab to the foreground we re-attempt any pending title generations.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        drainTitleRetries();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [drainTitleRetries]);

  // --- Handlers ---

  const showToast = useCallback(
    (message: string, type: "success" | "error" | "info" = "info", action?: { label: string; onClick: () => void }) => {
      setToast({ message, type, action });
    },
    []
  );

  // Mirrors iPhone AppState.judgeStatsExcludedHashes: load the server's
  // hash set once for this signed-in session, then keep local UI responsive.
  const [judgeStatsExcludedHashes, setJudgeStatsExcludedHashes] = useState<Set<string>>(
    () => new Set(),
  );
  const judgeStatsExcludesLoadedForRef = useRef<string | null>(null);
  const [judgePromptHashes, setJudgePromptHashes] = useState<Map<string, string>>(
    () => new Map(),
  );
  useEffect(() => {
    if (!isAuthenticated || !email || judgeStatsExcludesLoadedForRef.current === email) return;
    judgeStatsExcludesLoadedForRef.current = email;
    void fetchJudgeStatsExcludes()
      .then(({ excludes }) => setJudgeStatsExcludedHashes(new Set(excludes)))
      .catch(() => showToast("Failed to load judge stats settings", "error"));
  }, [email, isAuthenticated, showToast]);

  // Hash prompts in the page because it owns the exclusion set and API calls.
  // A Map also lets the expanded-card copy reuse the history card's result.
  useEffect(() => {
    let cancelled = false;
    const prompts = [...new Set(chatHistory.map((record) => record.prompt))];
    void Promise.all(prompts.map(async (prompt) => [prompt, await hashPrompt(prompt)] as const)).then((entries) => {
      if (!cancelled) setJudgePromptHashes(new Map(entries));
    });
    return () => { cancelled = true; };
  }, [chatHistory]);

  const setPromptStatsExcluded = useCallback((prompt: string, promptHash: string, excluded: boolean) => {
    setJudgeStatsExcludedHashes((previous) => {
      const next = new Set(previous);
      if (excluded) next.add(promptHash);
      else next.delete(promptHash);
      return next;
    });
    void setJudgeStatsExclude(prompt, excluded).catch(() => {
      setJudgeStatsExcludedHashes((previous) => {
        const next = new Set(previous);
        if (excluded) next.delete(promptHash);
        else next.add(promptHash);
        return next;
      });
      showToast(`Failed to ${excluded ? "remove from" : "count in"} stats`, "error");
    });
  }, [showToast]);

  // --- Judge: compare provider responses for the currently visible record ---
  // SSE consumer mirrors iOS ChatViewModel.judgeRecord. Server emits scoring →
  // synthesis_delta+ → verdict and persists the verdict server-side regardless
  // of client connectivity (detached worker pattern). On disconnect or non-
  // fatal error we poll GET /sessions/{id} for `judge_verdict` to recover.
  //
  // Reads history via `chatHistoryRef` (not the captured `chatHistory` state)
  // and accepts an optional `sessionId` override so the auto-judge path
  // (handleSendAndJudge) can pass the freshly-created sid + freshly-saved
  // record without depending on React's commit cycle. Without this, the
  // sequential `await sendMessage; await handleJudge` chain races: the
  // captured handleJudge sees null `activeSessionId` (new chat) and an
  // empty `chatHistory`, then bails with "No active session".
  const handleJudge = useCallback(async (opts?: { isAuto?: boolean; sessionId?: string; isTemporary?: boolean }) => {
    if (chatHistoryRef.current[chatHistoryRef.current.length - 1]?.mode === "deep_research") {
      return;
    }
    const isTemporary = opts?.isTemporary === true;
    const sessionId = opts?.sessionId || activeSessionId;
    // For temp chats the server doesn't need a sessionId — we send
    // records inline. For non-temp, we MUST have one or there's nothing
    // to look up.
    if (!sessionId && !isTemporary) {
      showToast("No active session", "error");
      return;
    }
    // Per-session bookkeeping key. Temp chats use a sentinel because
    // they have no real sid; there's only one temp chat at a time so
    // collisions are impossible.
    const trackingKey: string = isTemporary ? TEMP_JUDGE_KEY : (sessionId as string);
    if (judgingSessions.has(trackingKey)) return;
    // Capture press timestamp for post-hoc judge clicks (toolbar Judge or
    // direct handleJudge call). For auto-judge, handleSendAndJudge already
    // set this with an EARLIER timestamp (composer Judge tap, before
    // streaming) — don't overwrite it; we want total = full wall-clock,
    // not just judge phase.
    if (!autoJudgePressedAtRef.current.has(trackingKey)) {
      autoJudgePressedAtRef.current.set(trackingKey, Date.now());
    }
    let history = chatHistoryRef.current;
    if (history.length === 0) {
      // Defensive fallback for non-temp: chatHistoryRef can be stale on
      // edge cases (e.g. session switched mid-finalize, record save
      // raced with judge dispatch). Try one fresh loadHistory before
      // giving up — if the record exists server-side, this recovers.
      // For TEMP chats, the chat history is in-memory only; there's
      // no server-side history to load, so skip the fallback and just
      // bail with the same message (which will rarely fire because the
      // local-archive path in useChat appends synchronously before
      // sendMessage returns).
      if (!isTemporary && sessionId) {
        try {
          await loadHistory(sessionId);
        } catch {
          // ignore — fall through to length check
        }
        history = chatHistoryRef.current;
      }
      if (history.length === 0) {
        showToast(
          "No responses to judge — the record may not have been saved. Please try again.",
          "error"
        );
        return;
      }
    }

    const targetIdx = history.length - 1;
    const isAuto = opts?.isAuto === true;
    const targetRecord = history[targetIdx];
    const recordId = targetRecord?.serverRecordId;

    // Rotating scorer: pick from {anthropic, gemini, openai} ∩ providers that
    // produced output on this record. Synthesis is always Anthropic server-side.
    const judgedProviders = new Set(Object.keys(targetRecord?.responses || {}));
    const rotatingPool = ["anthropic", "gemini", "openai"].filter((p) => judgedProviders.has(p));
    const scoringProvider = rotatingPool.length > 0
      ? rotatingPool[Math.floor(Math.random() * rotatingPool.length)]
      : "anthropic";

    setJudgingSessions((prev) => { const next = new Set(prev); next.add(trackingKey); return next; });
    setJudgingScoringProviders((prev) => { const next = new Map(prev); next.set(trackingKey, scoringProvider); return next; });

    const clearJudgeState = () => {
      setJudgingSessions((prev) => { const next = new Set(prev); next.delete(trackingKey); return next; });
      setJudgingScoringProviders((prev) => { const next = new Map(prev); next.delete(trackingKey); return next; });
      setAutoJudgeSessions((prev) => { const next = new Set(prev); next.delete(trackingKey); return next; });
      autoJudgePressedAtRef.current.delete(trackingKey);
    };

    let pollHandle: { aborted: boolean } | null = null;
    let sawVerdict = false;
    let scrolledToVerdict = false;
    // True once the SSE stream has actually started emitting events. Used to
    // distinguish an initial-POST failure (no server-side judge exists → a
    // recovery poll is futile and just locks the toolbar Judge button) from a
    // mid-stream drop (server may still finish + persist → poll is worthwhile).
    let streamHadStarted = false;
    // Set once a fatal `error` event has already cleared judge state, so the
    // post-stream / catch logic doesn't re-arm a recovery poll on top of it.
    let fatalErrorHandled = false;

    // Recovery poll — runs at most 5 minutes. Self-cancels on verdict landing,
    // session navigation, or timeout. Mirrors iOS startJudgeVerdictPolling.
    // No-op for temp: there's no persisted server session to poll (the
    // session is queued for deletion); the SSE stream is the only path
    // to the verdict, and a 4xx/5xx there just means we lost it.
    const startRecoveryPoll = () => {
      if (isTemporary) {
        clearJudgeState();
        return;
      }
      if (pollHandle) return;
      const handle = { aborted: false };
      pollHandle = handle;
      (async () => {
        const startedAt = Date.now();
        while (!handle.aborted) {
          if (Date.now() - startedAt > 300_000) {
            clearJudgeState();
            return;
          }
          await new Promise((r) => setTimeout(r, 10_000));
          if (handle.aborted) return;
          if (viewedSessionRef.current !== sessionId) {
            clearJudgeState();
            return;
          }
          // sessionId is guaranteed non-null here: startRecoveryPoll
          // returns early when isTemporary, and non-temp paths bail
          // at handleJudge entry if sessionId is missing.
          if (!sessionId) return;
          try {
            const session = await fetchSession(sessionId);
            const records = session.records || [];
            if (targetIdx < records.length && records[targetIdx].judge_verdict) {
              setRecordJudgeVerdict(targetIdx, records[targetIdx].judge_verdict!);
              clearJudgeState();
              return;
            }
          } catch {
            // keep polling
          }
        }
      })();
    };

    try {
      // Two request shapes:
      //   - Temp: server has no session to look up (outboxDeleteSession may
      //     have already fired). Send the full chatHistory inline; the
      //     judge worker scores directly off the inline records. Mirrors
      //     iOS ChatViewModel.judgeRecord's isTemporary branch.
      //   - Persisted: standard session_id + record_index lookup.
      let request: JudgeRequest;
      if (isTemporary) {
        const history = chatHistoryRef.current;
        const inlineRecords = history.map((rec) => {
          const providers: Record<string, { output: string; usage?: Record<string, unknown> | null }> = {};
          for (const [pid, resp] of Object.entries(rec.responses)) {
            if (!resp.text) continue;
            providers[pid] = { output: resp.text, usage: resp.usage ?? null };
          }
          return {
            prompt: rec.prompt,
            providers,
            judge_verdict: rec.judgeVerdict ?? null,
          };
        });
        request = {
          session_id: null,
          record_index: null,
          scoring_provider: scoringProvider,
          records: inlineRecords,
          is_auto: isAuto,
        };
      } else {
        request = {
          session_id: sessionId,
          record_index: targetIdx,
          scoring_provider: scoringProvider,
          is_auto: isAuto,
        };
      }

      await streamJudge(request, (event) => {
        // Discard UI updates if the user has navigated away from this session.
        // The server-side detached worker keeps running and persists the verdict
        // regardless — when the user returns, loadHistory picks it up.
        if (viewedSessionRef.current !== sessionId) return;

        switch (event.type) {
          case "starting":
            break;
          case "scoring":
          case "synthesis_delta":
          case "synthesis_replace":
            setRecordJudgeVerdict(targetIdx, event.verdict);
            break;
          case "verdict": {
            sawVerdict = true;
            // Compute total_duration_ms client-side: button-tap → verdict
            // landed. Server doesn't know the click time, so this has to
            // happen here. For auto-judge, pressedAt is the composer Judge
            // tap (full send+judge wall-clock). For post-hoc judge, it's
            // the toolbar Judge tap (just the judge phase). Both useful.
            const pressedAt = autoJudgePressedAtRef.current.get(trackingKey);
            const enrichedVerdict = pressedAt
              ? { ...event.verdict, total_duration_ms: Date.now() - pressedAt }
              : event.verdict;
            setRecordJudgeVerdict(targetIdx, enrichedVerdict);
            // Defensive PATCH — server already persisted before emitting verdict,
            // but a race or late-cancellation could have skipped that. Idempotent
            // last-write-wins on judge_verdict. Skip for temp-chat: the
            // server session is queued for deletion (or already gone), so
            // patching would 404 / re-create dangling state. The verdict
            // lives in chatHistory in-memory for temp.
            if (recordId && !isTemporary && sessionId) {
              patchRecord(sessionId, recordId, { judge_verdict: enrichedVerdict }).catch(() => {
                // Backstop fire-and-forget — original verdict is already in
                // chatHistory and (hopefully) on the server. PATCH failure
                // means the server already had it, or the next loadHistory
                // will reconcile.
              });
            }
            // One-shot scroll to verdict
            if (!scrolledToVerdict) {
              scrolledToVerdict = true;
              setTimeout(() => {
                const cards = document.querySelectorAll("[data-judge-card]");
                const last = cards[cards.length - 1];
                if (last) last.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 200);
            }
            break;
          }
          case "error":
            if (event.fatal) {
              // Fatal — server worker bailed (parser failure, HTTP error, empty
              // synthesis). No verdict will arrive. Clear state immediately and
              // flag so the post-stream branch doesn't re-arm a recovery poll.
              setRecordJudgeVerdict(targetIdx, null);
              clearJudgeState();
              fatalErrorHandled = true;
              showToast(`Judge failed (${event.stage})`, "error");
            } else {
              // Non-fatal — server may still complete + persist. Poll to recover.
              startRecoveryPoll();
            }
            break;
        }
      }, undefined, () => {
        // First SSE event landed — the server-side judge is genuinely running.
        // From here a drop is mid-stream (recovery poll is meaningful).
        streamHadStarted = true;
      });

      // Stream ended cleanly without a verdict event — server closed early.
      // Skip entirely if a fatal `error` event already cleared judge state.
      if (!sawVerdict && !pollHandle && !fatalErrorHandled) {
        if (streamHadStarted) {
          // Stream had started then closed without a verdict — server may
          // still finish + persist. Poll to recover.
          startRecoveryPoll();
        } else {
          // Stream never emitted anything — treat as an initial-POST failure.
          // There is no server-side judge; clear state so the button frees.
          setRecordJudgeVerdict(targetIdx, null);
          clearJudgeState();
          showToast("Judge unavailable — please try again", "error");
        }
      }
    } catch (err) {
      const e = err as Error & { httpStatus?: number };
      const status = e.httpStatus ?? 0;
      if (status >= 400 && status < 500) {
        // 4xx = validation/auth/not-found. No verdict will ever arrive.
        setRecordJudgeVerdict(targetIdx, null);
        clearJudgeState();
        showToast(`Judge unavailable (HTTP ${status})`, "error");
      } else if (streamHadStarted) {
        // The SSE stream had already started before it dropped — the server
        // may still finish + persist the verdict; poll to recover it.
        startRecoveryPoll();
      } else {
        // Failure on the initial POST (a 5xx response after retries, or a
        // network error thrown before any SSE event). There is NO server-side
        // judge running, so a 5-minute recovery poll would just lock the
        // toolbar Judge button for nothing. Treat it like the 4xx path.
        setRecordJudgeVerdict(targetIdx, null);
        clearJudgeState();
        showToast(
          status >= 500
            ? `Judge unavailable (HTTP ${status})`
            : "Judge unavailable — please try again",
          "error"
        );
      }
    } finally {
      // If we landed a verdict, we're done (clearJudgeState idempotent on the
      // already-cleared poll case). If a poll started, it owns the lifecycle.
      if (sawVerdict) {
        clearJudgeState();
      }
    }
  }, [chatHistoryRef, judgingSessions, showToast, activeSessionId, setRecordJudgeVerdict, loadHistory]);

  // Mirror handleJudge into a ref so handleSendAndJudge (defined after
  // handleSendMessage) reads the current handleJudge identity at call time.
  // chatHistory updates change handleJudge's identity; without the ref, the
  // sequential `await sendMessage; await judge` flow would call a stale
  // handleJudge that doesn't see the just-saved record.
  const handleJudgeRef = useRef(handleJudge);
  useEffect(() => {
    handleJudgeRef.current = handleJudge;
  }, [handleJudge]);

  const handleNewChat = useCallback(() => {
    setActiveSession(null);
    clearChat();
    setPendingPrompt(null);
    clearCustomQueryState();
    setNewChatFolderId(null);
    setSentAttachments([]);
    // Temp-chat is a one-shot opt-in. New-chat means the user wants a
    // fresh persisted session — anything else surprises them when
    // they realize nothing was saved.
    setIsTempChat(false);
  }, [setActiveSession, clearChat, clearCustomQueryState]);

  // --- Global keyboard shortcuts (must be after handleNewChat definition) ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K — new chat
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        handleNewChat();
      }
      // Cmd+, or Ctrl+, — settings
      if ((e.metaKey || e.ctrlKey) && e.key === ",") {
        e.preventDefault();
        setShowSettings(true);
      }
      // Cmd+/ or Ctrl+/ — cycle mode
      if ((e.metaKey || e.ctrlKey) && e.key === "/") {
        e.preventDefault();
        setSelectedMode((prev) => {
          const availableModes = modes.length > 0
            ? ["auto", ...[...modes].sort((a, b) => a.order - b.order).map((mode) => mode.name)]
            : [...FALLBACK_MODES];
          const idx = availableModes.indexOf(prev);
          return availableModes[(idx + 1) % availableModes.length];
        });
      }
      // Escape — close modals
      if (e.key === "Escape") {
        if (showHelp) setShowHelp(false);
        else if (showSettings) setShowSettings(false);
        else if (showHistory) setShowHistory(false);
        else if (showFriendManager) setShowFriendManager(false);
        else if (showShareMenu) setShowShareMenu(false);
        else if (showManageSharing) setShowManageSharing(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNewChat, modes, showSettings, showHistory, showFriendManager, showShareMenu, showManageSharing, showHelp]);

  const handleNewChatInFolder = useCallback(
    (folderId: string) => {
      setActiveSession(null);
      clearChat();
      setPendingPrompt(null);
      clearCustomQueryState();
      setNewChatFolderId(folderId);
    },
    [setActiveSession, clearChat, clearCustomQueryState]
  );

  const handleSessionClick = useCallback(
    (id: string) => {
      setActiveSession(id);
      // Clear any "new chat in folder X" state staged before this click —
      // the user is now viewing an existing session, which has its own
      // folder assignment. Without this, switching from a half-set-up
      // new-chat-in-folder to an unfiled history item leaks the staged
      // folder onto the composer and risks misfiling follow-ups.
      setNewChatFolderId(null);
      setShowHistory(false);
      // NOTE: opening a session no longer bumps updated_at. History sorts by
      // last *activity* (a new message), not last access — so the list stays
      // stable when you merely read a chat (matches ChatGPT/Claude). Flagged
      // chats pin to the top; see RecentTab's sort.
    },
    [setActiveSession]
  );

  // --- Undo-delete helpers ---
  const executePendingDelete = useCallback(async (id: string, kind: "session" | "folder") => {
    pendingDeletesRef.current.delete(id);
    // Keep pendingDeleteIds filtering active until server confirms deletion,
    // so the 5-second session poll doesn't briefly re-show the item.
    try {
      if (kind === "folder") {
        const childIds = sessions.filter((s) => s.folder_id === id).map((s) => s.id);
        await deleteFolder(id);
        for (const sid of childIds) {
          // outbox-wrapped — never throws, queues on transient failure.
          await outboxDeleteSession(sid);
        }
        loadFolders();
      } else {
        const session = sessions.find((s) => s.id === id);
        if (session?.shared_by) {
          // Shared session — try leave, fall back to regular delete.
          // Both go through the outbox so transient failures retry.
          await outboxLeaveSharedSession(id);
          // Best-effort backup delete in case leave wasn't accepted
          // (e.g., user is the owner not a recipient — server returns
          // 404 but we still want it gone locally).
          await outboxDeleteSession(id);
        } else {
          await outboxDeleteSession(id);
        }
      }
    } catch (err) {
      console.error("Failed to delete:", err);
    }
    await loadSessions();
    // Clear the filter only after loadSessions has the updated list
    setPendingDeleteIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  }, [sessions, deleteSession, deleteFolder, loadSessions, loadFolders]);

  // -- Soft-delete a record (long-press / right-click → Delete query) -------
  // Mirrors iOS ChatViewModel.beginRecordDelete/commitPendingRecordDelete/
  // undoPendingRecordDelete. Optimistically removes the record from chat
  // history, shows an undo banner for 5s, and fires the server DELETE
  // when the timer elapses (or on send / on visibility=hidden).
  const commitPendingRecordDelete = useCallback(() => {
    const pd = recordPendingDeleteRef.current;
    if (!pd) return;
    recordPendingDeleteRef.current = null;
    setRecordPendingDelete(null);
    if (recordPendingDeleteTimerRef.current) {
      clearTimeout(recordPendingDeleteTimerRef.current);
      recordPendingDeleteTimerRef.current = null;
    }
    if (!pd.sessionId) return;  // temp chat — local-only removal was enough
    void (async () => {
      try {
        const resp = await deleteRecord(pd.sessionId, pd.recordId);
        if (resp.session_deleted) {
          // Last record removed → server cascade-deleted the session too.
          // Mirror that locally and refresh sidebar.
          try {
            await deleteSession(pd.sessionId);
          } catch (err) {
            console.error("Failed to mirror cascade delete locally:", err);
          }
          void loadSessions();
        }
      } catch (err) {
        console.error("Failed to commit record delete:", err);
      }
    })();
  }, [deleteSession, loadSessions]);

  const undoPendingRecordDelete = useCallback(() => {
    const pd = recordPendingDeleteRef.current;
    if (!pd) return;
    recordPendingDeleteRef.current = null;
    setRecordPendingDelete(null);
    if (recordPendingDeleteTimerRef.current) {
      clearTimeout(recordPendingDeleteTimerRef.current);
      recordPendingDeleteTimerRef.current = null;
    }
    restoreRecord(pd.record, pd.recordIndex);
  }, [restoreRecord]);

  const beginRecordDelete = useCallback(
    (sessionId: string, recordId: string) => {
      // Stack-prevention: any prior pending delete commits immediately
      // before we open a new window. Without this a second long-press
      // within 5s would orphan the first.
      if (recordPendingDeleteRef.current) {
        commitPendingRecordDelete();
      }
      const removed = removeRecord(recordId);
      if (!removed) return;
      const promptPreview = removed.record.prompt.slice(0, 80);
      const pd = {
        sessionId,
        recordId,
        promptPreview,
        record: removed.record,
        recordIndex: removed.index,
      };
      recordPendingDeleteRef.current = pd;
      setRecordPendingDelete(pd);
      recordPendingDeleteTimerRef.current = setTimeout(() => {
        commitPendingRecordDelete();
      }, 5000);
    },
    [removeRecord, commitPendingRecordDelete]
  );

  // Background-clear timer — mirrors iOS FaceOffApp.scenePhase handler
  // (FaceOffApp.swift:217-228). When the tab has been hidden long
  // enough, treat the user's return as "I moved on" and start a fresh
  // chat. Thresholds match iOS: 10 min normal, 5 min temp. Skipped
  // when a query is in flight so an active stream isn't nuked.
  // Refs (not deps) for `isStreaming` and `isTempChat` so background and
  // send-to-judge safety callbacks always read the latest value without
  // re-registering on every render.
  const backgroundedAtRef = useRef<number | null>(null);
  const isStreamingRef = useRef(isStreaming);
  const isTempChatRef = useRef(isTempChat);
  useEffect(() => { isStreamingRef.current = isStreaming; }, [isStreaming]);
  useEffect(() => { isTempChatRef.current = isTempChat; }, [isTempChat]);
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden") {
        if (backgroundedAtRef.current === null) {
          backgroundedAtRef.current = Date.now();
        }
      } else if (document.visibilityState === "visible") {
        const at = backgroundedAtRef.current;
        backgroundedAtRef.current = null;
        if (at === null) return;
        const awaySeconds = (Date.now() - at) / 1000;
        // Active stream / background job — don't yank the chat out
        // from under the user. iOS canonical: chatViewModel.isBusy.
        if (isStreamingRef.current) return;
        const wasTemp = isTempChatRef.current;
        if (wasTemp && awaySeconds >= 300) {
          // Temp chat away 5+ min — clear temp + start fresh.
          setIsTempChat(false);
          setTempChatFolderId(null);
          handleNewChat();
        } else if (!wasTemp && awaySeconds >= 600) {
          // Normal chat away 10+ min — start fresh.
          handleNewChat();
        }
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [handleNewChat]);

  // Commit on tab hidden — equivalent to iOS's scenePhase=.background
  // hook. Without this, closing/backgrounding the tab during the 5s
  // window would leave the record gone locally but the server DELETE
  // never fires.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden" && recordPendingDeleteRef.current) {
        commitPendingRecordDelete();
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [commitPendingRecordDelete]);

  // Click-anywhere-else dismisses the right-click context menu. We
  // attach to document so the menu closes even when the user clicks
  // outside the chat surface.
  useEffect(() => {
    if (!promptContextMenu) return;
    const handler = () => setPromptContextMenu(null);
    // Defer one tick so the click that opened the menu doesn't
    // immediately close it.
    const id = setTimeout(() => {
      document.addEventListener("click", handler);
      document.addEventListener("contextmenu", handler);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("click", handler);
      document.removeEventListener("contextmenu", handler);
    };
  }, [promptContextMenu]);

  const undoPendingDelete = useCallback((id: string) => {
    const entry = pendingDeletesRef.current.get(id);
    if (entry) {
      clearTimeout(entry.timeoutId);
      pendingDeletesRef.current.delete(id);
    }
    setPendingDeleteIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    setToast(null);
  }, []);

  const softDelete = useCallback((id: string, kind: "session" | "folder", label: string) => {
    // Cancel any existing pending delete for this id
    const existing = pendingDeletesRef.current.get(id);
    if (existing) clearTimeout(existing.timeoutId);

    // If deleting the active session, switch away
    if (kind === "session" && activeSessionId === id) {
      setActiveSession(null);
    }

    // Hide from UI
    setPendingDeleteIds((prev) => new Set(prev).add(id));

    // Schedule real delete after 5 seconds
    const timeoutId = setTimeout(() => executePendingDelete(id, kind), 5000);
    pendingDeletesRef.current.set(id, { timeoutId, kind });

    // Show undo toast
    showToast(`${label} deleted`, "success", {
      label: "Undo",
      onClick: () => undoPendingDelete(id),
    });
  }, [activeSessionId, setActiveSession, executePendingDelete, undoPendingDelete, showToast]);

  const handleSessionDelete = useCallback(
    (id: string) => {
      const session = sessions.find((s) => s.id === id);
      softDelete(id, "session", session?.title || "Chat");
    },
    [sessions, softDelete]
  );

  const handleBulkDelete = useCallback(async (ids: string[]) => {
    // Bulk delete: optimistic UI + parallel background API calls.
    //
    // Why this matters: deleting 50+ items sequentially blocked the UI
    // for ~10s waiting on 50 sequential network round-trips. Now we
    // remove from the visible list IMMEDIATELY (via pendingDeleteIds)
    // and fire the actual deletes in parallel in the background. The
    // server's per-user asyncio.Lock serializes them safely. Each call
    // is outbox-wrapped, so transient failures queue for retry.
    //
    // Bulk delete is undo-less (multi-select already requires intentional
    // confirmation), unlike softDelete which schedules a 5-second timer.

    const folderIdsSet = new Set(folders.map((f) => f.id));
    const idSet = new Set(ids);

    // Resolve which API call each id needs BEFORE we mutate state so the
    // share_by / folder lookups are accurate against the current view.
    type DeleteJob =
      | { kind: "folder"; id: string; childSessionIds: string[] }
      | { kind: "shared"; id: string }
      | { kind: "session"; id: string };
    const jobs: DeleteJob[] = ids.map((id) => {
      if (folderIdsSet.has(id)) {
        const childSessionIds = sessions
          .filter((s) => s.folder_id === id)
          .map((s) => s.id);
        return { kind: "folder", id, childSessionIds };
      }
      const session = sessions.find((s) => s.id === id);
      if (session?.shared_by) return { kind: "shared", id };
      return { kind: "session", id };
    });

    // Optimistic UI updates (synchronous, fires before any await).
    //   1. Add every targeted id to pendingDeleteIds — visible filters
    //      (`visibleSessions`/`visibleFolders` at the render layer) hide
    //      them immediately AND suppress polled re-adds during the
    //      background-delete window.
    //   2. If the currently-active session is in the batch, switch away
    //      so we don't render a deleted session's chat shell.
    //   3. Show the success toast right away — the user already
    //      confirmed via the multi-select delete affordance.
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      // Folders' children also need to be hidden during the gap so the
      // sidebar doesn't briefly show orphaned sessions before the cascade
      // delete completes.
      for (const job of jobs) {
        if (job.kind === "folder") {
          for (const childId of job.childSessionIds) next.add(childId);
        }
      }
      return next;
    });
    if (activeSessionId && idSet.has(activeSessionId)) {
      setActiveSession(null);
    }
    showToast(`Deleted ${ids.length} item${ids.length !== 1 ? "s" : ""}`, "success");

    // Background: fire all deletes in parallel. outbox wrappers never
    // throw — transient failures land in the local queue for retry.
    // Folder + child-session deletes WITHIN a single folder job are
    // sequential (folder first, then children) because that matches
    // the iOS pattern and avoids an orphan-window where the folder
    // is gone but its children still claim folder_id of a now-deleted
    // folder for ~50 ms. Across jobs we parallelize.
    void (async () => {
      let serverFailures = 0;
      await Promise.all(
        jobs.map(async (job) => {
          try {
            if (job.kind === "folder") {
              await deleteFolder(job.id);
              await Promise.all(
                job.childSessionIds.map((sid) => outboxDeleteSession(sid))
              );
            } else if (job.kind === "shared") {
              await outboxLeaveSharedSession(job.id);
              await outboxDeleteSession(job.id);
            } else {
              await outboxDeleteSession(job.id);
            }
          } catch (err) {
            serverFailures++;
            console.error("[bulkDelete] failed:", job, err);
          }
        })
      );

      // Reconcile with server state — picks up anything that drifted
      // (outbox-queued failures, concurrent edits, etc.).
      await Promise.all([loadSessions(), loadFolders()]);

      // Drop pendingDeleteIds entries only AFTER reconciliation so the
      // visible-filter doesn't re-show a server-side-still-present item
      // during the gap.
      setPendingDeleteIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        for (const job of jobs) {
          if (job.kind === "folder") {
            for (const childId of job.childSessionIds) next.delete(childId);
          }
        }
        return next;
      });

      // If outbox drained everything but some still failed server-side,
      // emit a quieter follow-up toast so the user knows the initial
      // optimistic "Deleted N" wasn't fully accurate.
      if (serverFailures > 0) {
        showToast(
          `${serverFailures} item${serverFailures !== 1 ? "s" : ""} could not be deleted — will retry`,
          "error"
        );
      }
    })();
  }, [
    sessions,
    folders,
    activeSessionId,
    setActiveSession,
    deleteFolder,
    loadSessions,
    loadFolders,
    showToast,
  ]);

  const handleBulkMoveToFolder = useCallback(async (ids: string[], folderId: string) => {
    // Capture each session's source folder BEFORE mutations so we can
    // regen every folder that lost a member. Mirrors iOS batchMove.
    const priorFolderIds = new Set<string>();
    for (const id of ids) {
      const prior = sessions.find(s => s.id === id)?.folder_id;
      if (prior) priorFolderIds.add(prior);
    }
    // Single batch call instead of N sequential PUTs: one file write under
    // one lock, and batch "move" deliberately does NOT bump updated_at, so
    // the moved chats keep their place in Recent instead of jumping to top.
    try {
      await batchSessions({ action: "move", session_ids: ids, folder_id: folderId || "" });
    } catch (err) {
      console.error("Failed to move sessions:", ids, err);
    }
    loadSessions();
    // Regen destination (if into a folder) + every distinct source.
    // Fire-and-forget — server runs the summary async.
    if (folderId) {
      void generateFolderContext(folderId).catch(() => {});
    }
    for (const priorId of priorFolderIds) {
      if (priorId !== folderId) {
        void generateFolderContext(priorId).catch(() => {});
      }
    }
    showToast(`Moved ${ids.length} chat${ids.length !== 1 ? "s" : ""}`, "success");
  }, [loadSessions, showToast, sessions]);

  // Single-session three-dots → "Move to Folder" submenu: move directly to a
  // folder ("" = Unfiled) or, via "+ Create New", open the name dialog.
  const handleMoveSessionToFolder = useCallback((sessionId: string, folderId: string) => {
    void handleBulkMoveToFolder([sessionId], folderId);
  }, [handleBulkMoveToFolder]);
  const handleMoveSessionToNewFolder = useCallback((sessionId: string) => {
    setNewFolderForSession(sessionId);
  }, []);

  // Multi-select → "New Folder…": create the named folder, then move the
  // selected chats into it (reuses the batch-move path for regen + toast).
  const handleBulkMoveToNewFolder = useCallback(async (ids: string[], name: string) => {
    try {
      const folder = await createFolder({ name: name.trim() || "New Folder", order: folders.length });
      await handleBulkMoveToFolder(ids, folder.id);
    } catch (err) {
      showToast("Failed to create folder", "error");
      console.error("Failed to move to new folder:", err);
    }
  }, [createFolder, folders.length, handleBulkMoveToFolder, showToast]);

  const handleSessionRename = useCallback(
    async (id: string, title: string) => {
      try {
        await updateSession(id, { title });
      } catch (err) {
        showToast("Failed to rename chat", "error");
        console.error("Failed to rename chat:", err);
      }
    },
    [updateSession, showToast]
  );

  const handleSessionFlag = useCallback(
    async (id: string) => {
      const session = sessions.find((s) => s.id === id);
      if (!session) return;
      try {
        await updateSession(id, { flagged: !session.flagged });
      } catch (err) {
        showToast("Failed to update chat", "error");
        console.error("Failed to flag chat:", err);
      }
    },
    [sessions, updateSession, showToast]
  );

  // Set to a just-created folder's id so the history UI opens it straight into
  // its (text-selected) rename input. Cleared once the folder row consumes it.
  const [newlyCreatedFolderId, setNewlyCreatedFolderId] = useState<string | null>(null);

  const handleCreateFolder = useCallback(async () => {
    try {
      const folder = await createFolder({ name: "New Folder", order: folders.length });
      setNewlyCreatedFolderId(folder.id);
      showToast("Folder created", "success");
    } catch (err) {
      showToast("Failed to create folder", "error");
      console.error("Failed to create folder:", err);
    }
  }, [createFolder, folders.length, showToast]);

  const handleRenameFolder = useCallback(
    async (id: string, name: string) => {
      try {
        await updateFolder(id, { name });
      } catch (err) {
        showToast("Failed to rename folder", "error");
        console.error("Failed to rename folder:", err);
      }
    },
    [updateFolder, showToast]
  );

  // Toggle the grouping separator drawn above a folder.
  const handleSetFolderSeparator = useCallback(
    async (id: string, value: boolean) => {
      try {
        await updateFolder(id, { separator_before: value });
      } catch (err) {
        showToast("Failed to update separator", "error");
        console.error("Failed to update folder separator:", err);
      }
    },
    [updateFolder, showToast]
  );

  const handleReorderFolders = useCallback(
    async (orderedIds: string[]) => {
      try {
        // Single atomic request — no race condition possible
        await apiReorderFolders(orderedIds);
        await loadFolders();
      } catch (err) {
        console.error("Failed to reorder folders:", err);
      }
    },
    [loadFolders]
  );

  const handleDeleteFolder = useCallback(
    (id: string) => {
      const folder = folders.find((f) => f.id === id);
      softDelete(id, "folder", folder?.name || "Folder");
    },
    [folders, softDelete]
  );

  const handleFolderContextClick = useCallback(async () => {
    if (!effectiveFolderId) return;
    try {
      const memory = await fetchFolderMemory(effectiveFolderId);
      setFolderContextModal({
        folderId: effectiveFolderId,
        title: activeFolderName || "Folder",
        items: [...memory.items].sort((a, b) => b.date.localeCompare(a.date)),
      });
    } catch (err) {
      console.error("Failed to load folder memory:", err);
    }
  }, [effectiveFolderId, activeFolderName]);

  const handleDeleteFolderMemoryItem = useCallback(async (itemId: string) => {
    if (!folderContextModal) return;
    try {
      const result = await deleteFolderMemoryItem(folderContextModal.folderId, itemId);
      if (result.ok) {
        setFolderContextModal((current) => current
          ? { ...current, items: current.items.filter((item) => item.id !== itemId) }
          : null);
      }
    } catch (err) {
      console.error("Failed to delete folder memory item:", err);
    }
  }, [folderContextModal]);

  const handleFilesSelected = useCallback((files: File[]) => {
    setPendingAttachments((prev) => [...prev, ...files]);
  }, []);

  const handleRemoveAttachment = useCallback((index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const beginDeepResearchPolling = useCallback((run: ActiveDeepResearch) => {
    if (deepResearchPollingJobRef.current === run.jobId) return;
    if (deepResearchPollTimerRef.current !== null) window.clearInterval(deepResearchPollTimerRef.current);
    deepResearchPollingJobRef.current = run.jobId;
    let consecutivePollFailures = 0;
    const poll = async () => {
      try {
        const status = await pollDeepResearch(run.jobId);
        consecutivePollFailures = 0;
        const current = activeDeepResearchRef.current;
        if (current?.jobId !== run.jobId) return;
        const updated = { ...current, status };
        activeDeepResearchRef.current = updated;
        setActiveDeepResearch(updated);
        if (!isDeepResearchTerminal(status.status)) return;
        if (deepResearchPollTimerRef.current !== null) window.clearInterval(deepResearchPollTimerRef.current);
        deepResearchPollTimerRef.current = null;
        deepResearchPollingJobRef.current = null;
        setPendingPrompt(null);
        if (status.status === "done" && run.sessionId && !run.isTemporary) {
          await loadHistory(run.sessionId);
          void loadSessions();
          activeDeepResearchRef.current = null;
          setActiveDeepResearch(null);
        }
      } catch (err) {
        console.error("Failed to poll Deep Research:", err);
        consecutivePollFailures += 1;
        if (consecutivePollFailures === DEEP_RESEARCH_POLL_FAILURES_BEFORE_NOTICE) {
          showToast("Unable to check Deep Research status. Retrying…", "error");
        }
      }
    };
    deepResearchPollTimerRef.current = window.setInterval(() => { void poll(); }, 15_000);
    void poll();
  }, [loadHistory, loadSessions, showToast]);

  useEffect(() => () => {
    if (deepResearchPollTimerRef.current !== null) window.clearInterval(deepResearchPollTimerRef.current);
  }, []);

  const handleDeepResearch = useCallback(async (
    prompt: string,
    opts?: { sessionId?: string; autoSelected?: boolean }
  ): Promise<string | null> => {
    if (activeDeepResearch?.status.status === "running") {
      showToast("Deep Research is already running in the background.", "info");
      return null;
    }
    pendingPromptTime.current = new Date();
    setPendingPrompt(prompt);
    setIsPreparingDeepResearch(true);
    const attachmentsIgnored = pendingAttachments.length > 0;
    commitPendingRecordDelete();
    // Auto may have just created a session, but React state has not committed
    // yet; use that sid so the reroute cannot create a duplicate session.
    let sid = opts?.sessionId ?? activeSessionId;
    const isNewSession = !sid && !isTempChat;
    if (!sid) {
      try {
        const session = await apiCreateSessionDirect({ title: "New Chat", ...(newChatFolderId ? { folder_id: newChatFolderId } : {}) });
        sid = session.id;
        setActiveSession(sid);
        setNewChatFolderId(null);
      } catch (err) {
        console.error("Failed to create Deep Research session:", err);
        showToast("Failed to create session", "error");
        setPendingPrompt(null);
        setIsPreparingDeepResearch(false);
        return null;
      }
    }

    if (attachmentsIgnored) {
      showToast(
        opts?.autoSelected
          ? "Auto selected Deep Research, so attached files were not used."
          : "Attachments are not used in Deep Research.",
        "info"
      );
    }

    const fallbackBrief = (): DeepResearchBriefResponse => ({ framing: "", sub_questions: [], from_fallback: true, brief_text: prompt, expected_cost_low_usd: 0, expected_cost_high_usd: 0, expected_duration_seconds: 0 });
    let brief: DeepResearchBriefResponse = fallbackBrief();
    let briefReady = false;
    let briefJobId: string | null = null;
    try {
      briefJobId = (await startDeepResearchBrief(prompt)).brief_job_id;
    } catch (err) {
      if ((err as Error & { status?: number }).status === 404) {
        try {
          brief = await draftDeepResearchBrief(prompt);
          briefReady = true;
        } catch (draftErr) {
          console.warn("Deep Research brief failed; researching the question as written:", draftErr);
          brief = fallbackBrief();
        }
      } else {
        console.warn("Deep Research brief failed; researching the question as written:", err);
        brief = fallbackBrief();
      }
    }
    if (briefJobId) try {
      const deadline = Date.now() + 180_000;
      while (Date.now() < deadline) {
        const briefJob = await pollDeepResearchBrief(briefJobId);
        if (briefJob.status === "done" && briefJob.result) {
          brief = briefJob.result;
          briefReady = true;
          break;
        }
        if (briefJob.status === "error") throw new Error(briefJob.error || "Deep Research brief failed");
        await new Promise<void>((resolve) => window.setTimeout(resolve, 2_000));
      }
      if (!briefReady) throw new Error("Deep Research brief timed out");
    } catch (err) {
      console.warn("Deep Research brief failed; researching the question as written:", err);
      brief = fallbackBrief();
    }

    try {
      const clientRequestId = createDeepResearchClientRequestId();
      const started = await startDeepResearch({
        brief_text: brief.brief_text,
        question: prompt,
        session_id: sid!,
        ...(isTempChat ? { is_temporary: true } : {}),
        client_request_id: clientRequestId,
      });
      // Auto uploaded these for classification; clear them once the rerouted
      // Deep Research run starts so the composer matches a normal send.
      if (opts?.autoSelected) {
        setPendingAttachments([]);
      }
      setIsPreparingDeepResearch(false);
      if (isNewSession && sid) {
        const capturedSid = sid;
        const capturedPrompt = prompt;
        summarizePrompt(capturedPrompt, {})
          .then((res) => {
            if (res.summary && res.summary !== "Untitled") {
              return apiUpdateSession(capturedSid, { title: res.summary });
            }
          })
          .then(() => {
            loadSessions();
          })
          .catch((err) => {
            console.error("Title generation failed:", err);
            queueTitleRetry(capturedSid, capturedPrompt);
          });
      }
      const active: ActiveDeepResearch = {
        jobId: started.job_id,
        sessionId: sid || "",
        isTemporary: isTempChat,
        prompt,
        brief: brief.brief_text,
        fromFallback: brief.from_fallback,
        expectedDurationSeconds: brief.expected_duration_seconds,
        startedAt: Date.now(),
        status: { status: "running", stages: brief.stages },
        cancelling: false,
        attachmentsIgnored,
        startedInThisTab: true,
      };
      activeDeepResearchRef.current = active;
      setActiveDeepResearch(active);
      beginDeepResearchPolling(active);
      return sid;
    } catch (err) {
      console.error("Failed to start Deep Research:", err);
      showToast("Failed to start Deep Research", "error");
      setPendingPrompt(null);
      setIsPreparingDeepResearch(false);
      return null;
    }
  }, [activeDeepResearch, activeSessionId, isTempChat, newChatFolderId, pendingAttachments.length, setActiveSession, showToast, commitPendingRecordDelete, loadSessions, queueTitleRetry, beginDeepResearchPolling]);

  // A server-owned Deep Research job outlives a reload. Only replace the
  // viewed session when the reader is on a genuinely blank chat.
  useEffect(() => {
    if (!isAuthenticated) return;
    const resume = async () => {
      if (activeDeepResearchRef.current || deepResearchActiveFetchRef.current) return;
      deepResearchActiveFetchRef.current = true;
      try {
        const { active } = await fetchActiveDeepResearch();
        if (!active || activeDeepResearchRef.current) return;
        // The server persists created_at as time.time() epoch seconds. Keep
        // accepting ISO-8601 too, for compatibility with older responses.
        const startedAt = active.created_at === null
          ? NaN
          : typeof active.created_at === "number"
            ? active.created_at * 1_000
            : new Date(active.created_at).getTime();
        const expectedFinishAt = active.expected_finish_at ? new Date(active.expected_finish_at).getTime() : NaN;
        const resumed: ActiveDeepResearch = {
          jobId: active.job_id,
          sessionId: active.session_id ?? "",
          isTemporary: active.is_temporary,
          prompt: active.question ?? "",
          brief: "",
          fromFallback: false,
          expectedDurationSeconds: Number.isFinite(expectedFinishAt) ? Math.max(0, Math.round((expectedFinishAt - startedAt) / 1_000)) : undefined,
          startedAt: Number.isFinite(startedAt) ? startedAt : Date.now(),
          status: {
            status: active.status,
            lane: active.lane,
            stages: active.stages,
            stage: active.stage,
            stage_label: active.stage_label,
            stages_done: active.stages_done,
            stage_started_at: active.stage_started_at,
            detail: active.detail,
            early_look: active.early_look,
            expected_finish_at: active.expected_finish_at,
            cost_usd: active.cost_usd,
            run_counts: active.run_counts,
          },
          cancelling: false,
          attachmentsIgnored: false,
        };
        if (active.session_id && activeSessionId !== active.session_id && (!activeSessionId || chatHistoryRef.current.length === 0)) {
          setActiveSession(active.session_id);
        }
        if (active.question) {
          pendingPromptTime.current = new Date(startedAt);
          setPendingPrompt(active.question);
        }
        activeDeepResearchRef.current = resumed;
        setActiveDeepResearch(resumed);
        beginDeepResearchPolling(resumed);
      } catch (err) {
        console.error("Failed to resume Deep Research:", err);
      } finally {
        deepResearchActiveFetchRef.current = false;
      }
    };
    void resume();
    const onVisible = () => {
      if (document.visibilityState === "visible") void resume();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isAuthenticated, activeSessionId, setActiveSession, chatHistoryRef, beginDeepResearchPolling]);

  const handleCancelDeepResearch = useCallback(async () => {
    const active = activeDeepResearch;
    if (!active?.jobId || isDeepResearchTerminal(active.status.status)) return;
    setActiveDeepResearch({ ...active, cancelling: true });
    try {
      await cancelDeepResearch(active.jobId);
      // The cancel response only confirms receipt; the next status poll is authoritative.
      const status = await pollDeepResearch(active.jobId);
      setActiveDeepResearch((current) => current?.jobId === active.jobId ? { ...current, cancelling: false, status } : current);
    } catch (err) {
      console.error("Failed to cancel Deep Research:", err);
      setActiveDeepResearch((current) => current?.jobId === active.jobId ? { ...current, cancelling: false } : current);
    }
  }, [activeDeepResearch]);

  const handleSendMessage = useCallback(
    async (
      prompt: string,
      opts?: {
        onSessionCreated?: (sid: string) => void;
        onDeepResearchRerouted?: () => void;
        autoJudge?: boolean;
        // One-shot provider override — when set, this send targets exactly
        // these providers instead of the persistent `selectedProviders`
        // state, WITHOUT mutating it. Used by the "A" fave-send button.
        providersOverride?: string[];
      }
    ): Promise<string | null> => {
      if (selectedMode === "deep_research") {
        return handleDeepResearch(prompt);
      }
      // Providers to target for THIS send only (fave-send passes a one-shot
      // override; normal sends use the persistent selection).
      const effectiveProviders = followUpProviderIDs(
        opts?.providersOverride ?? selectedProviders,
        providers.filter((provider) => provider.enabled).map((provider) => provider.id),
        chatHistory.some((record) => record.mode === "deep_research"),
        selectedMode === "deep_research"
      );
      // Commit any pending soft-delete BEFORE dispatching so the new
      // turn's server-built conversation_history reflects it. Mirrors
      // the iOS sendPrompt/sendPromptAndAutoJudge guards.
      commitPendingRecordDelete();
      // Returns the session id used for this send (newly-created or existing),
      // or null on failure. Auto-judge needs this to fire the /judge call
      // without waiting on React state updates. The optional onSessionCreated
      // callback fires the instant a fresh session is created (before any
      // streaming begins) so callers can synchronously associate per-session
      // state — used by handleSendAndJudge to put the new sid into
      // autoJudgeSessions before the active-streaming render block evaluates.
      let sid = activeSessionId;
      const isNewSession = !sid && !isTempChat;

      // Capture the custom-query prompt BEFORE clearing it — the title
      // generator (below) needs it so a custom-query session ("Apple"
      // fired through the "Pitch as investment" query) gets a title
      // that reflects the actual task, not just the user's trigger
      // word. (Tests 4.2 + 4.5.)
      const capturedCustomQueryPrompt = activeCustomQueryPrompt;
      clearCustomQueryState();

      // Create session if needed (use direct API to avoid premature session list refresh).
      // Title is "New Chat" until Summarize resolves (INV-5, decision 5). No
      // immediate truncation — that is the last-resort fallback only after
      // 60s of sustained Summarize failure (see drainTitleRetries).
      if (isNewSession) {
        try {
          const createData: Record<string, string> = { title: "New Chat" };
          if (newChatFolderId) createData.folder_id = newChatFolderId;
          if (folderContextCache?.versionId) createData.folder_context_version_id = folderContextCache.versionId;
          const session = await apiCreateSessionDirect(createData);
          sid = session.id;
          setActiveSession(sid);
          // Tell the caller (e.g. handleSendAndJudge) the new sid the moment
          // it exists, so per-session state (autoJudgeSessions) can be set
          // synchronously and the active-streaming render block sees it
          // without any cross-session state leak.
          opts?.onSessionCreated?.(sid);
          setNewChatFolderId(null);
        } catch (err) {
          showToast("Failed to create session", "error");
          console.error("Failed to create session:", err);
          return null;
        }
      }

      // Upload attachments if any
      let attachmentIds: string[] | undefined;
      let uploadedAttachments: import("@/app/lib/types").AttachmentResponse[] = [];
      if (pendingAttachments.length > 0) {
        try {
          const result = await uploadAttachments(pendingAttachments);
          attachmentIds = result.attachments.map((a) => a.id);
          uploadedAttachments = result.attachments;
        } catch (err) {
          showToast("Failed to upload attachments", "error");
          console.error("Failed to upload attachments:", err);
          return null;
        }
      }

      // Generate title in parallel with the query (only on new-session sends).
      // Moved AFTER attachment upload so attachment_ids is available — the
      // server uses it to enrich the summarizer's input with extracted
      // document text, producing a descriptive title for prompts like
      // "Summarize this article" instead of a generic one (Test 4.5).
      // Also passes capturedCustomQueryPrompt for Test 4.2.
      if (isNewSession && sid) {
        const capturedSid = sid;
        const capturedPrompt = prompt;
        const capturedAttachmentIds = attachmentIds;
        summarizePrompt(capturedPrompt, {
          attachmentIds: capturedAttachmentIds,
          customQueryPrompt: capturedCustomQueryPrompt || undefined,
        })
          .then((res) => {
            if (res.summary && res.summary !== "Untitled") {
              return apiUpdateSession(capturedSid, { title: res.summary });
            }
          })
          .then(() => {
            loadSessions();
          })
          .catch((err) => {
            console.error("Title generation failed; will retry:", err);
            queueTitleRetry(capturedSid, capturedPrompt);
          });
      }

      // Show prompt immediately
      pendingPromptTime.current = new Date();
      setPendingPrompt(prompt);

      // ---------- S2: Auto mode classify + dispatch ----------
      // For Auto, run /chat/classify to get per-provider mode + params, then
      // build per-provider requests with the classifier's picks. Dispatch
      // mode used by sendMessage is the heaviest provider mode (so the right
      // stream-vs-background path runs).
      let dispatchMode = selectedMode;
      let providerRequests: ProviderRequest[];
      if (selectedMode === "auto") {
        // Build attachment metadata for the classifier. Server-side
        // PyMuPDF extraction during /attachments upload populates
        // text_chars/page_count/sampled_pages directly on the upload
        // response — far more reliable than client-side PDF.js (which
        // returned 0 chars on Adobe Acrobat / PScript5-produced PDFs
        // with subsetted Identity-H fonts). PDF.js path retained as a
        // last-resort fallback if upload didn't return stats (e.g.
        // server hit an extraction error).
        let classifyAttachments: ClassifyAttachment[] | undefined;
        if (pendingAttachments.length > 0) {
          const stats = await Promise.all(
            pendingAttachments.map(async (file, i) => {
              const att: ClassifyAttachment = {
                filename: file.name,
                mime_type: file.type || "application/octet-stream",
                size_bytes: file.size,
              };
              const upload = uploadedAttachments[i];
              if (upload?.text_chars != null) att.text_chars = upload.text_chars;
              if (upload?.page_count != null) att.page_count = upload.page_count;
              if (upload?.sampled_pages != null) att.sampled_pages = upload.sampled_pages;
              if (att.text_chars == null && file.type === "application/pdf") {
                // Server extraction missing — fall back to client-side.
                try {
                  const s = await computeAttachmentStats(file);
                  if (s.textChars != null) att.text_chars = s.textChars;
                  if (s.pageCount != null) att.page_count = s.pageCount;
                  if (s.sampledPages != null) att.sampled_pages = s.sampledPages;
                } catch {
                  // Best-effort; classifier handles missing stats gracefully.
                }
              }
              return att;
            })
          );
          classifyAttachments = stats;
        }

        // Build a 5-turn conversation history snippet for the classifier so
        // it can see context. Mirrors iOS performAutoFlow at
        // ChatViewModel.swift:606-609.
        const classifyHistory: Array<{ role: string; content: string }> = [];
        const recentRecords = chatHistory.slice(-5);
        for (const r of recentRecords) {
          classifyHistory.push({ role: "user", content: r.prompt });
          if (r.judgeVerdict) {
            classifyHistory.push({ role: "assistant", content: r.judgeVerdict.synthesis });
          } else {
            const firstResp = r.responses[r.providerOrder[0]];
            if (firstResp?.text) {
              classifyHistory.push({
                role: "assistant",
                content: firstResp.text.slice(0, 2000),
              });
            }
          }
        }

        let decisions: Record<string, ClassifyDecision> = {};
        try {
          const classifyResp = await classifyChat({
            prompt,
            targets: effectiveProviders,
            history: classifyHistory.length > 0 ? classifyHistory : undefined,
            attachments: classifyAttachments,
            custom_query_prompt: activeCustomQueryPrompt || undefined,
          });
          decisions = classifyResp.decisions;
        } catch {
          // Classifier failed — fall back to thinking for everyone. Dispatch
          // continues, just without the classifier's mode picks.
          for (const pid of effectiveProviders) {
            decisions[pid] = { mode: "thinking" };
          }
        }

        // Deep Research is a single-model flow, not a per-provider chat
        // dispatch. One matching classifier decision starts it exactly once.
        if (Object.values(decisions).some((decision) => decision.mode === "deep_research")) {
          // Tell Auto+Judge that this send has no chat record to judge yet.
          opts?.onDeepResearchRerouted?.();
          return handleDeepResearch(prompt, { sessionId: sid ?? undefined, autoSelected: true });
        }

        // Compute heaviest mode across decisions for the dispatch path.
        let maxMode = "thinking";
        let maxRank = Number.NEGATIVE_INFINITY;
        for (const d of Object.values(decisions)) {
          const r = modeRank(d.mode, modes);
          if (r > maxRank) {
            maxRank = r;
            maxMode = d.mode;
          }
        }
        dispatchMode = maxMode;

        providerRequests = effectiveProviders
          .map((pid) => {
            const d = decisions[pid] || { mode: "thinking" };
            return buildProviderRequestFor(pid, d.mode, providers, preferences, d.params);
          })
          .filter(Boolean) as ProviderRequest[];
      } else {
        providerRequests = effectiveProviders
          .map((pid) => buildProviderRequestFor(pid, selectedMode, providers, preferences))
          .filter(Boolean) as ProviderRequest[];
      }

      if (providerRequests.length === 0) {
        showToast("No providers available for this mode", "error");
        return null;
      }

      // Build conversation history from chat records
      // Use judge synthesis when available (replaces individual provider responses)
      const conversationHistory: Array<{ role: string; content: string }> = [];
      for (let i = 0; i < chatHistory.length; i++) {
        const record = chatHistory[i];
        conversationHistory.push({ role: "user", content: record.prompt });
        if (record.judgeVerdict) {
          conversationHistory.push({ role: "assistant", content: record.judgeVerdict.synthesis });
        } else {
          const firstProvider = record.providerOrder[0];
          const resp = record.responses[firstProvider];
          if (resp?.text) {
            conversationHistory.push({ role: "assistant", content: resp.text });
          }
        }
      }

      try {
        const attachmentFileNames = pendingAttachments.length > 0
          ? pendingAttachments.map(f => f.name)
          : undefined;
        // One-shot composer mode: the manual pick applied to this query.
        // Revert to the user's Settings "Default Mode" (typically Auto) so
        // every follow-up / new query starts from the default again.
        setSelectedMode(resolveDefaultMode(preferences?.default_mode, modes));
        await sendMessage(
          prompt,
          providerRequests,
          dispatchMode,
          sid || "",
          conversationHistory,
          attachmentIds,
          folderContextCache?.summary,
          folderContextCache?.versionId,
          activeCustomQueryPrompt || undefined,
          attachmentFileNames,
          attachmentIds,
          isTempChat,
          opts?.autoJudge
        );

        // Move pending attachments to "sent" display, then clear pending
        if (pendingAttachments.length > 0) {
          setSentAttachments(
            pendingAttachments.map((f, i) => ({
              name: f.name,
              id: attachmentIds?.[i],
            }))
          );
        }
        setPendingPrompt(null);
        setPendingAttachments([]);

        // For temp chats, delete the session so it doesn't persist.
        // outbox-wrapped so a network blip doesn't leak the temp chat
        // into the user's history; queued op will fire on reconnect.
        if (isTempChat && sid) {
          void outboxDeleteSession(sid);
        }

        // Refresh sessions list (for non-new sessions; new sessions refresh when title resolves)
        if (!isTempChat && !isNewSession) {
          loadSessions();
        }

        // Piggyback on the "successful next API call" hook (decision 5): a
        // successful send is a strong signal the network is healthy again, so
        // drain any queued title retries now.
        drainTitleRetries();
      } catch (err) {
        // Pass the underlying error message through so save-failure cases
        // (record never persisted) say so explicitly instead of a generic
        // "Failed to send message". Critical because a save-failure is
        // recoverable by retrying, but the user needs to know they need to.
        const msg = err instanceof Error && err.message ? err.message : "Failed to send message";
        showToast(msg, "error");
        console.error("Failed to send message:", err);
        return null;
      }
      return sid;
    },
    [
      activeSessionId,
      setActiveSession,
      selectedProviders,
      providers,
      selectedMode,
      chatHistory,
      sendMessage,
      loadSessions,
      showToast,
      setPendingPrompt,
      pendingAttachments,
      isTempChat,
      deleteSession,
      drainTitleRetries,
      handleDeepResearch,
      queueTitleRetry,
    ]
  );

  // Composer "A" button — one-shot send to the favorite provider only.
  // NOT a mode toggle: fires a single multi-turn-normal send targeting just
  // the first selected provider (via handleSendMessage's providersOverride),
  // without touching the persistent `selectedProviders`. No judging (a single
  // answer has nothing to compare against).
  const handleSendFavorite = useCallback(
    (prompt: string) => {
      const fave = firstSelectedProviderInfo?.id;
      if (!fave) return;
      void handleSendMessage(prompt, { providersOverride: [fave] });
    },
    [firstSelectedProviderInfo, handleSendMessage]
  );

  // Composer Judge button — sends the prompt, captures the sid that handle-
  // SendMessage actually used (newly-created or existing), then fires /judge
  // with that sid + is_auto=true. Passing the sid explicitly avoids the
  // React-state race that the ref-based handleJudge alone can't dodge: in a
  // new-chat flow, `activeSessionId` starts null and `setActiveSession(sid)`
  // queues but doesn't commit before this function continues, so the
  // handleJudge captured in handleJudgeRef sees a stale null sid and bails.
  // iOS canonical: sendPromptAndAutoJudge + recordPersistedToServer chain in
  // ChatViewModel.swift.
  const handleSendAndJudge = useCallback(
    async (prompt: string) => {
      // The disabled path is the pre-auto-judge behavior: send normally,
      // without creating judge intent or a synthesis-card loading state.
      if (!isFeatureEnabled("auto_judge")) {
        await handleSendMessage(prompt);
        return;
      }
      // Deep Research is a single server-owned answer: never arm auto-judge
      // state or call /judge for it, even if this handler is reached directly.
      if (selectedMode === "deep_research") {
        await handleDeepResearch(prompt);
        return;
      }
      const chatHasDeepResearch = chatHistory.some((record) => record.mode === "deep_research");
      const anthropicEnabled = providers.some((provider) => provider.id === "anthropic" && provider.enabled);
      const providerIDsForThisSend = followUpProviderIDs(
        selectedProviders,
        providers.filter((provider) => provider.enabled).map((provider) => provider.id),
        chatHasDeepResearch,
        false
      );
      // Only the Deep Research follow-up rule suppresses judging. If
      // Anthropic is disabled, retain the existing Send+Judge behavior.
      if (chatHasDeepResearch && anthropicEnabled && providerIDsForThisSend.length === 1 && providerIDsForThisSend[0] === "anthropic") {
        await handleSendMessage(prompt);
        return;
      }
      // Commit any pending soft-delete first — same rule as handleSendMessage.
      commitPendingRecordDelete();
      // Capture press timestamp NOW for total_duration_ms (composer-tap →
      // verdict landed). For an existing session we know the sid; for a
      // new chat we'll re-key onto the new sid in onSessionCreated below.
      const pressedAt = Date.now();
      const initialSid = viewedSessionRef.current;
      let judgeSessionId = initialSid;
      let reroutedToDeepResearch = false;

      // S6 safety net: if the send-then-judge handoff hangs for 5 min
      // after response generation ends (e.g., the record save never lands
      // due to a stuck network), clear the synthesis-card spinner state so
      // the user isn't staring at "Judging…" forever. A legitimately
      // streaming turn has not reached that handoff yet, so defer the check.
      // The timer is disarmed the moment handleSendMessage returns and we
      // hand off to handleJudge (which has its own SSE-side watchdogs).
      let safetyTimer: ReturnType<typeof setTimeout> | null = null;
      const armSafetyTimer = (sid: string) => {
        if (safetyTimer) clearTimeout(safetyTimer);
        safetyTimer = setTimeout(() => {
          safetyTimer = null;
          if (isTurnLegitimatelyInProgress(isStreamingRef.current)) {
            armSafetyTimer(sid);
            return;
          }
          setAutoJudgeSessions((prev) => { const next = new Set(prev); next.delete(sid); return next; });
          autoJudgePressedAtRef.current.delete(sid);
          showToast("Judge unavailable — record could not be saved", "error");
        }, 5 * 60 * 1000);
      };
      const disarmSafetyTimer = () => {
        if (safetyTimer) {
          clearTimeout(safetyTimer);
          safetyTimer = null;
        }
      };

      // For TEMP chats: no server session is created, so initialSid is
      // null and onSessionCreated never fires. Use the TEMP sentinel
      // for the bookkeeping maps so the spinner/handoff state can be
      // tracked across the send. handleJudge will use the same sentinel.
      if (isTempChat) {
        setAutoJudgeSessions((prev) => { const next = new Set(prev); next.add(TEMP_JUDGE_KEY); return next; });
        autoJudgePressedAtRef.current.set(TEMP_JUDGE_KEY, pressedAt);
        armSafetyTimer(TEMP_JUDGE_KEY);
      } else if (initialSid) {
        // Existing session — add immediately so the synthesis card appears
        // from t=0 of streaming.
        setAutoJudgeSessions((prev) => { const next = new Set(prev); next.add(initialSid); return next; });
        autoJudgePressedAtRef.current.set(initialSid, pressedAt);
        armSafetyTimer(initialSid);
      }
      // For new chats, hook into session creation. handleSendMessage fires
      // this callback the moment apiCreateSessionDirect returns, BEFORE any
      // streaming starts — so the sid lands in autoJudgeSessions before the
      // active-streaming render block runs. No global "pending" flag is
      // needed, which means this auto-judge intent can't leak into
      // concurrent normal-Send queries in other sessions.
      const sid = await handleSendMessage(prompt, {
        autoJudge: true,
        onSessionCreated: (newSid) => {
          judgeSessionId = newSid;
          setAutoJudgeSessions((prev) => { const next = new Set(prev); next.add(newSid); return next; });
          autoJudgePressedAtRef.current.set(newSid, pressedAt);
          // Re-key the safety timer onto the freshly-minted sid.
          armSafetyTimer(newSid);
        },
        onDeepResearchRerouted: () => {
          // The classifier rerouted before a Deep Research record exists, so
          // remove any armed auto-judge state and skip the /judge handoff.
          reroutedToDeepResearch = true;
        },
      });
      if (reroutedToDeepResearch) {
        disarmSafetyTimer();
        if (judgeSessionId) {
          setAutoJudgeSessions((prev) => { const next = new Set(prev); next.delete(judgeSessionId!); return next; });
          autoJudgePressedAtRef.current.delete(judgeSessionId);
        }
        return;
      }
      // Temp chats: handleSendMessage returns null (no server session
      // created). That's NOT a failure — chatHistory was populated
      // locally by sendMessage's no-sid branch. Skip the bail and call
      // handleJudge with isTemporary=true so it uses the inline-records
      // path.
      if (!sid && !isTempChat) {
        disarmSafetyTimer();
        if (initialSid) {
          setAutoJudgeSessions((prev) => { const next = new Set(prev); next.delete(initialSid); return next; });
        }
        return;
      }
      // Send succeeded — handoff to handleJudge, which owns the SSE
      // lifecycle and has its own clearJudgeState path. No further
      // safety-timer protection needed at this layer.
      disarmSafetyTimer();
      if (isTempChat) {
        await handleJudgeRef.current({ isAuto: true, isTemporary: true });
      } else {
        await handleJudgeRef.current({ isAuto: true, sessionId: sid! });
      }
    },
    [handleSendMessage, showToast, isTempChat, selectedMode, handleDeepResearch, chatHistory, providers]
  );

  // Per-record, per-provider Retry (OQ-S6-a). Builds a minimal
  // ProviderRequest from the failed record's stored model/options (those
  // were what the server actually used) and delegates to useChat's
  // retryProvider, which handles resume-then-rerun + PATCH.
  //
  // Returned callback matches the `(providerId) => Promise<void>` signature
  // that ResponseCard expects. We curry recordIndex at the call site in the
  // chat-history loop.
  const buildRetryHandler = useCallback(
    (recordIndex: number) => {
      return async (providerId: string): Promise<void> => {
        if (!activeSessionId) return;
        const record = chatHistory[recordIndex];
        if (!record) return;
        const prior = record.responses[providerId];
        if (!prior) return;

        // Rebuild conversation history as `sendMessage` does — excluding this
        // record and later, since the retry *is* for this turn.
        const conversationHistory: Array<{ role: string; content: string }> = [];
        for (let i = 0; i < recordIndex; i++) {
          const r = chatHistory[i];
          conversationHistory.push({ role: "user", content: r.prompt });
          if (r.judgeVerdict) {
            conversationHistory.push({ role: "assistant", content: r.judgeVerdict.synthesis });
          } else {
            const firstProvider = r.providerOrder[0];
            const resp = r.responses[firstProvider];
            if (resp?.text) {
              conversationHistory.push({ role: "assistant", content: resp.text });
            }
          }
        }

        const providerRequest: ProviderRequest = {
          provider: providerId,
          model: prior.model || "",
          ...(prior.options ? { options: prior.options as Record<string, unknown> } : {}),
        };

        try {
          await retryProvider(activeSessionId, recordIndex, providerRequest, conversationHistory);
        } catch (err) {
          showToast("Retry failed", "error");
          console.error("Retry failed:", err);
        }
      };
    },
    [activeSessionId, chatHistory, retryProvider, showToast]
  );

  // --- Render ---

  // Loading state
  if (authLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "var(--bg)",
        }}
      >
        <div className="spinner spinner--lg" />
      </div>
    );
  }

  // Not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  // Sidebar props (shared between desktop sidebar and mobile history sheet).
  // Sessions appear in the list the moment they're committed to the server
  // (INV-4, decision 4). Title may still be "New Chat" while Summarize is
  // in-flight or retrying (INV-5, decision 5); that's intentional and
  // visible to the user. Only soft-delete pending ids are filtered.
  const visibleSessions = sessions.filter((s) => !pendingDeleteIds.has(s.id));
  const visibleFolders = folders.filter((f) => !pendingDeleteIds.has(f.id));

  const sidebarProps = {
    sessions: visibleSessions,
    folders: visibleFolders,
    activeSessionId: activeSessionId ?? undefined,
    isLoading: sessionsLoading,
    onSessionClick: handleSessionClick,
    onSessionDelete: handleSessionDelete,
    onSessionRename: handleSessionRename,
    onSessionFlag: handleSessionFlag,
    onNewChatInFolder: handleNewChatInFolder,
    onCreateFolder: handleCreateFolder,
    onRenameFolder: handleRenameFolder,
    onSetFolderSeparator: handleSetFolderSeparator,
    onDeleteFolder: handleDeleteFolder,
    onReorderFolders: handleReorderFolders,
    onSettingsClick: () => setShowSettings(true),
    onMoveToFolder: (id: string) => setMoveSessionId(id),
    onMoveSessionToFolder: handleMoveSessionToFolder,
    onMoveToNewFolder: handleMoveSessionToNewFolder,
    onShare: (id: string) => { setActiveSession(id); setShowShareMenu(true); },
    currentUserEmail: email ?? undefined,
    searchQuery,
    searchResults,
    isSearching,
    onBulkDelete: handleBulkDelete,
    onBulkMoveToFolder: handleBulkMoveToFolder,
    onBulkMoveToNewFolder: handleBulkMoveToNewFolder,
    newlyCreatedFolderId,
    onAutoEditConsumed: () => setNewlyCreatedFolderId(null),
  };

  // Determine which responses to show (history records + active streaming)
  const hasActiveResponses = Object.keys(activeResponses).length > 0;
  const activeDeepResearchToolCounts = getDeepResearchDisplayedToolCounts(activeDeepResearch?.status);
  const showActiveDeepResearch = !!activeDeepResearch && (
    activeDeepResearch.sessionId === activeSessionId ||
    (activeDeepResearch.startedInThisTab === true && !activeDeepResearch.sessionId)
  );
  const showPendingPrompt = !!pendingPrompt && (!activeDeepResearch || showActiveDeepResearch);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100dvh", background: "var(--bg)" }}>
      {/* Full-width top header bar */}
      <header className="top-header-bar">
        {isMobile && (
          <div style={{ position: "absolute", left: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
            <button
              className="btn-ghost"
              onClick={() => setShowHistory(true)}
              aria-label="Open history"
            >
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <rect x="1" y="2.5" width="14" height="1.5" rx="0.75" />
                <rect x="1" y="7.25" width="14" height="1.5" rx="0.75" />
                <rect x="1" y="12" width="14" height="1.5" rx="0.75" />
              </svg>
            </button>
            <button className="btn-ghost" onClick={() => setShowFriendManager(true)} aria-label="Friends" style={{ position: "relative" }}>
              <svg width="17" height="17" viewBox="0 0 20 20" fill="var(--text-secondary)">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
              {pendingFriendCount > 0 && (
                <span style={{ ...BADGE_PILL_STYLE, position: "absolute", top: "-2px", right: "-2px" }}>
                  {formatBadgeCount(pendingFriendCount)}
                </span>
              )}
            </button>
          </div>
        )}
        {!isMobile && (
          <div style={{ position: "absolute", left: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
            <button className="btn-ghost" onClick={() => setSidebarCollapsed(prev => !prev)} aria-label="Toggle sidebar">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="16" height="14" rx="2" />
                <path d="M7 3v14" />
              </svg>
            </button>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "calc(var(--sidebar-width) - 55px)",
                  padding: "6px 8px 6px 24px",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--bg)",
                  color: "var(--text-primary)",
                  fontSize: "12px",
                  outline: "none",
                }}
              />
              <svg width="12" height="12" viewBox="0 0 20 20" fill="var(--text-tertiary)" style={{ position: "absolute", left: "6px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} style={{ position: "absolute", right: "4px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-tertiary)", cursor: "pointer", padding: "1px", fontSize: "12px", lineHeight: 1 }} aria-label="Clear search">&times;</button>
              )}
            </div>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
          <img src="/trio-icon.png" alt="Trio" width={29} height={29} style={{ borderRadius: "5px" }} />
          <span style={{ fontWeight: 600, fontSize: "19px", color: "var(--text-primary)" }}>Trio</span>
        </div>
        <div style={{ position: "absolute", right: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
          {isAuthenticated && helpViewCount <= 5 && (
            <div style={{ position: "relative" }}>
              <button
                className="btn-ghost"
                onClick={() => { setShowHelpCallout(false); setShowHelp(true); }}
                aria-label="Help"
              >
                <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5">
                  <circle cx="10" cy="10" r="8" />
                  <path d="M7.5 7.5a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 3.5" strokeLinecap="round" />
                  <circle cx="10" cy="14.5" r="0.6" fill="var(--text-secondary)" stroke="none" />
                </svg>
              </button>
              {showHelpCallout && (
                <div
                  onClick={() => setShowHelpCallout(false)}
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    right: 0,
                    background: "var(--accent)",
                    color: "white",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    fontSize: "12px",
                    lineHeight: 1.4,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                    zIndex: 1000,
                    cursor: "pointer",
                    width: "260px",
                  }}
                >
                  Click ? for a quick overview. After a few logins, this will move to &quot;Help&quot; in Settings
                </div>
              )}
            </div>
          )}
          {/* Judge button — balance scale */}
          {(() => {
            // Per-session spinner state: temp chats use the TEMP sentinel
            // key for bookkeeping since they have no real activeSessionId.
            const judgingThisSession = isTempChat
              ? judgingSessions.has(TEMP_JUDGE_KEY)
              : (activeSessionId ? judgingSessions.has(activeSessionId) : false);
            // Block while any provider in the current record is still
            // streaming — judging a partial response is meaningless.
            const queryInFlight = isStreaming;
            // Temp chats are valid even with !activeSessionId — the
            // inline-records path needs only chatHistory.
            const noSessionContext = !activeSessionId && !isTempChat;
            const judgeDisabled = judgingThisSession || queryInFlight || chatHistory.length === 0 || noSessionContext;
            return chatHistory[chatHistory.length - 1]?.mode === "deep_research" ? null : (
          <button
            className="btn-ghost"
            onClick={() => { void handleJudge({ isTemporary: isTempChat }); }}
            disabled={judgeDisabled}
            aria-label="Judge responses"
            title={
              noSessionContext
                ? "Select a session first"
                : chatHistory.length === 0
                ? "No responses to judge"
                : queryInFlight
                ? "Wait for all responses to finish"
                : "Judge responses"
            }
            style={{ opacity: judgeDisabled && !judgingThisSession ? 0.35 : undefined }}
          >
            {judgingThisSession ? (
              <svg width="17" height="17" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 1.2s linear infinite" }}>
                <circle cx="8" cy="8" r="6.5" stroke="var(--accent)" strokeWidth="1.5" opacity="0.25" />
                <path d="M14.5 8a6.5 6.5 0 00-6.5-6.5" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20" />
                <path d="M2 7h20" />
                <path d="M5 7l-3 9h8L7 7" />
                <path d="M17 7l-3 9h8L19 7" />
                <path d="M2 16c0 1.5 2.2 3 4 3s4-1.5 4-3" />
                <path d="M14 16c0 1.5 2.2 3 4 3s4-1.5 4-3" />
                <circle cx="12" cy="2" r="1.5" fill="var(--text-secondary)" stroke="none" />
                <path d="M9 22h6" />
              </svg>
            )}
          </button>
            );
          })()}
          <button className="btn-ghost" onClick={() => setShowShareMenu(true)} aria-label="Share" style={{ position: "relative" }}>
            <svg width="17" height="17" viewBox="0 0 20 20" fill="var(--text-secondary)">
              <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
            </svg>
            {unseenSharedCount > 0 && (
              <span style={{ ...BADGE_PILL_STYLE, position: "absolute", top: "-2px", right: "-2px" }}>
                {formatBadgeCount(unseenSharedCount)}
              </span>
            )}
          </button>
          {activeSession?.shared && !activeSession.shared_by && (
            <button className="btn-ghost" onClick={() => setShowManageSharing(true)} aria-label="Manage sharing">
              <svg width="17" height="17" viewBox="0 0 20 20" fill="var(--accent)">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
            </button>
          )}
          {!isMobile && (
            <button className="btn-ghost" onClick={() => setShowFriendManager(true)} aria-label="Friends" style={{ position: "relative" }}>
              <svg width="17" height="17" viewBox="0 0 20 20" fill="var(--text-secondary)">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
              {pendingFriendCount > 0 && (
                <span style={{ position: "absolute", top: "-2px", right: "-2px", minWidth: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", background: "#ef4444", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "0 4px", lineHeight: 1 }}>
                  {pendingFriendCount}
                </span>
              )}
            </button>
          )}
          <button className="btn-ghost" onClick={() => setShowSettings(true)} aria-label="Settings" style={{ position: "relative" }}>
            <svg width="17" height="17" viewBox="0 0 20 20" fill="var(--text-secondary)">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.062 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/>
            </svg>
            {isAdmin && adminBadgeCounts.total > 0 && (
              <span style={{ ...BADGE_PILL_STYLE, position: "absolute", top: "-2px", right: "-2px" }}>
                {formatBadgeCount(adminBadgeCounts.total)}
              </span>
            )}
          </button>
        </div>
      </header>

      <div className={`app-layout${isMobile ? " mobile" : ""}`} onClick={() => showHelpCallout && setShowHelpCallout(false)}>
      {/* Desktop Sidebar */}
      {!isMobile && !sidebarCollapsed && <Sidebar {...sidebarProps} />}

      {/* Main Content */}
      <div className="main-content">
        {/* Chat area */}
        <div className={`chat-area${isStreaming ? " streaming" : ""}`} ref={chatAreaRef}>
          {/* Loading history spinner */}
          {isLoadingHistory && (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="spinner spinner--lg" />
            </div>
          )}

          {/* Provider load error */}
          {!authLoading && !sessionsLoading && providers.length === 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "24px",
                color: "var(--text-secondary)",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              Unable to load providers. Please try refreshing.
            </div>
          )}

          {/* Empty state */}
          {!isLoadingHistory && chatHistory.length === 0 && !hasActiveResponses && !showPendingPrompt && !showActiveDeepResearch && (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                color: "var(--text-tertiary)",
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                opacity={0.4}
              >
                <circle cx="24" cy="24" r="20" />
                <path d="M16 20h16M16 28h10" strokeLinecap="round" />
              </svg>
              <span style={{ fontSize: "15px" }}>
                {activeSessionId ? "Start a conversation" : "Create a new chat to get started"}
              </span>
            </div>
          )}

          {/* Chat history records */}
          {chatHistory.map((record: ChatRecord, idx: number) => (
            <div key={idx} style={{ display: "contents" }}>
              {/* Timestamp + prompt group — snap point */}
              <div ref={idx === chatHistory.length - 1 ? queryAreaRef : undefined} className="snap-start" style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <div style={{ fontSize: "12px", color: "var(--text-tertiary)", padding: "0 4px" }}>
                {record.timestamp.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at {record.timestamp.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                {record.createdBy && record.createdBy !== email && (
                  <> | Sent by {record.createdBy}</>
                )}
              </div>
              <div
                className="prompt-box"
                style={{
                  position: "relative",
                  padding: "10px 38px 10px 14px",
                  background: "var(--surface-hover)",
                  borderRadius: "var(--radius-lg)",
                  color: "var(--text)",
                  fontSize: "14px",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
                onContextMenu={(e) => {
                  // Only open the menu when we have both the session
                  // id and a serverRecordId — temp/in-flight records
                  // with no server identity can't be DELETEd.
                  if (!activeSessionId || !record.serverRecordId) return;
                  e.preventDefault();
                  setPromptContextMenu({
                    x: e.clientX,
                    y: e.clientY,
                    sessionId: activeSessionId,
                    recordId: record.serverRecordId,
                  });
                }}
              >
                {record.prompt}
                <PromptCopyButton text={record.prompt} />
              </div>
              {/* Persistent attachment chips for this turn — clickable
                  to open the attachment in a new tab. Mirrors iOS, where
                  attachments are shown inline with the prompt of each
                  archived turn (Test 4.5). */}
              {record.attachmentNames && record.attachmentNames.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                  {record.attachmentNames.map((name, i) => {
                    const id = record.attachmentIds?.[i];
                    return (
                      <span
                        key={i}
                        onClick={id ? async (e) => {
                          e.stopPropagation();
                          try {
                            const resp = await fetch(`${process.env.NEXT_PUBLIC_API_BASE || ""}/attachments/${id}`, {
                              headers: getAuthHeaders(),
                            });
                            if (!resp.ok) return;
                            const blob = await resp.blob();
                            window.open(URL.createObjectURL(blob), "_blank");
                          } catch { /* ignore */ }
                        } : undefined}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: "5px",
                          fontSize: "12px", fontWeight: 500,
                          color: "var(--accent)",
                          background: "var(--accent-muted)",
                          border: "1px solid color-mix(in srgb, var(--accent) 50%, transparent)",
                          borderRadius: "999px", padding: "4px 10px",
                          cursor: id ? "pointer" : "default",
                        }}
                        title={id ? "Click to view" : name}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                        {name}
                      </span>
                    );
                  })}
                </div>
              )}
              </div>

              {record.mode === "deep_research" ? (() => {
                const details = deepResearchMetadata(record);
                const response = record.responses[record.providerOrder[0]];
                return <DeepResearchCard
                  report={response?.text}
                  sources={details.sources}
                  brief={details.brief}
                  fromFallback={details.fromFallback}
                  terminalMessage={response?.error}
                  status={response?.error ? "error" : undefined}
                  metadata={{
                    model: response?.model,
                    durationSeconds: details.durationSeconds,
                    costUsd: details.costUsd,
                    corpusDocumentsRead: details.corpusDocumentsRead,
                    corpusCitedCount: details.corpusDocumentsCited,
                    sourceCount: details.sourceCount,
                  }}
                  onOpenCorpusDocument={setCorpusDocumentSource}
                  recordId={record.serverRecordId}
                  ttsProviderId={record.providerOrder[0]}
                  onReadAloud={!ttsEnabled ? undefined : handleReadAloud}
                  isTTSActive={isCardTTSActive(record.serverRecordId, record.providerOrder[0])}
                  ttsProgress={isCardTTSActive(record.serverRecordId, record.providerOrder[0]) ? ttsProgress : undefined}
                  isTTSCached={isCardTTSCached(record.serverRecordId, record.providerOrder[0])}
                />;
              })() : <>
              {/* Layout split (S5):
                    - record.judgeVerdict.auto OR active auto-judge in flight on
                      the latest record → synthesis card is leftmost in 4-up
                      (desktop grid / mobile peek pager).
                    - Otherwise → existing layout: 3 provider cards, JudgeCard
                      as footer below (post-hoc judge surface).
                  iOS canonical: CompactChatView.swift:864-1133. */}
              {(() => {
                const isLatest = idx === chatHistory.length - 1;
                const interrupted = isInterruptedRecord(record, isLatest);
                // Temp chats use the TEMP_JUDGE_KEY sentinel in all
                // the bookkeeping maps — match here so the spinner +
                // 4-up auto-surface layout fire correctly for temp.
                const judgeMapKey = isTempChat ? TEMP_JUDGE_KEY : activeSessionId;
                const judgeInflightOnLatest =
                  !interrupted && isLatest && !!judgeMapKey && judgingSessions.has(judgeMapKey);
                // v1.6 web parity (match iOS): render a stable skeleton for an
                // in-flight turn on history-revisit instead of the blank → 3 → 4
                // pop-in. Provider side: any expected provider without a
                // complete response yet. Judge side: the latest turn of a
                // Send+Judge session with no verdict yet — covers the
                // server-owned-judge case where THIS client never fired /judge
                // (so judgingSessions is empty), which the old isLoading gate
                // missed.
                const recordInFlight = record.providerOrder.some((pid) => {
                  const r = record.responses[pid];
                  return !r || !r.isComplete;
                });
                const judgeExpectedPending =
                  isLatest &&
                  !interrupted &&
                  !record.judgeVerdict &&
                  // Reload-proof: prefer the server-persisted flag (survives a
                  // fresh load); fall back to this browser's in-session intent.
                  (record.autoJudgeRequested === true ||
                    (!!judgeMapKey && autoJudgeSessions.has(judgeMapKey)));
                const isAutoSurface =
                  record.judgeVerdict?.auto === true ||
                  judgeExpectedPending ||
                  (judgeInflightOnLatest &&
                    !!judgeMapKey &&
                    autoJudgeSessions.has(judgeMapKey));

                const interruptedNotice = interrupted ? (
                  <div
                    style={{ padding: "12px", borderRadius: "10px", background: "color-mix(in srgb, var(--text-secondary) 8%, transparent)", display: "grid", gap: "8px" }}
                  >
                    <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>This answer was interrupted before it finished.</span>
                    <button
                      type="button"
                      onClick={() => setComposerPrefill((previous) => ({
                        text: record.prompt,
                        id: (previous?.id ?? 0) + 1,
                      }))}
                      style={{ justifySelf: "start", border: 0, background: "transparent", color: "var(--accent)", fontWeight: 600, padding: 0, cursor: "pointer" }}
                    >
                      Ask again
                    </button>
                  </div>
                ) : null;

                const judgeCardEl = (
                  <JudgeCard
                    verdict={record.judgeVerdict || null}
                    providers={providers}
                    promptHash={judgePromptHashes.get(record.prompt)}
                    isStatsExcluded={judgeStatsExcludedHashes.has(judgePromptHashes.get(record.prompt) ?? "")}
                    onSetStatsExcluded={(promptHash, excluded) =>
                      setPromptStatsExcluded(record.prompt, promptHash, excluded)
                    }
                    judgeStartedAt={record.judgeStartedAt}
                    // A record that ALREADY has a verdict must never render as
                    // "Judging…". Without the `!record.judgeVerdict` guard, a
                    // stale `judgingSessions` entry (e.g. a scheduled chat whose
                    // server-owned judge already finished) makes
                    // judgeInflightOnLatest true and flashes the in-flight card
                    // over the present verdict until the flag clears. The verdict
                    // is authoritative — if it's here, judging is done.
                    isLoading={!record.judgeVerdict && (judgeInflightOnLatest || (judgeExpectedPending && !recordInFlight))}
                    isWaitingForOutputs={!record.judgeVerdict && judgeExpectedPending && recordInFlight}
                    scoringProvider={
                      judgeMapKey ? judgingScoringProviders.get(judgeMapKey) : undefined
                    }
                    recordId={record.serverRecordId}
                    onReadAloud={!ttsEnabled ? undefined : handleReadAloud}
                    isTTSActive={isCardTTSActive(record.serverRecordId, record.judgeVerdict ? judgeSpeechCardId(record.judgeVerdict) : "judge")}
                    ttsProgress={isCardTTSActive(record.serverRecordId, record.judgeVerdict ? judgeSpeechCardId(record.judgeVerdict) : "judge") ? ttsProgress : undefined}
                    isTTSCached={isCardTTSCached(record.serverRecordId, record.judgeVerdict ? judgeSpeechCardId(record.judgeVerdict) : "judge")}
                    onDelete={
                      record.judgeVerdict && activeSessionId
                        ? async () => {
                            try {
                              await deleteJudgeVerdict(activeSessionId, idx);
                              setRecordJudgeVerdict(idx, null);
                            } catch {
                              showToast("Failed to delete verdict", "error");
                            }
                          }
                        : undefined
                    }
                    onRegenerate={
                      record.judgeVerdict && isLatest
                        ? () => {
                            const inheritedAuto = record.judgeVerdict?.auto === true;
                            // For auto-regenerate, also re-arm the auto session
                            // flag so the layout stays in 4-up while the new
                            // judge run streams. Capture press timestamp now
                            // so total_duration_ms reflects the regenerate
                            // run (not the original judge).
                            if (inheritedAuto && activeSessionId) {
                              setAutoJudgeSessions((prev) => {
                                const next = new Set(prev);
                                next.add(activeSessionId);
                                return next;
                              });
                              autoJudgePressedAtRef.current.set(activeSessionId, Date.now());
                            }
                            setRecordJudgeVerdict(idx, null);
                            void handleJudge({ isAuto: inheritedAuto, isTemporary: isTempChat });
                          }
                        : undefined
                    }
                    onExpand={!isMobile ? () => setExpandedCard({ kind: "history-judge", recordIdx: idx }) : undefined}
                  />
                );

                // Page-dot color for the synthesis card on mobile peek pager:
                // winner color when verdict landed, else accent.
                const winnerProvider = record.judgeVerdict
                  ? providers.find(
                      (p) =>
                        p.id === record.judgeVerdict!.winner ||
                        p.display_name === record.judgeVerdict!.winner
                    )
                  : null;
                const synthesisDotColor = winnerProvider?.color || "var(--accent)";

                return (
                  <>
                    <div className="snap-start">
                      {isMobile ? (
                        <ResponsePager
                          key={`${record.serverRecordId ?? idx}-${pagerEpoch}`}
                          responses={record.responses}
                          providerOrder={record.providerOrder}
                          providers={providers}
                          isStreaming={recordInFlight}
                          mode={record.mode}
                          onRetry={buildRetryHandler(idx)}
                          synthesisCard={isAutoSurface ? judgeCardEl : undefined}
                          synthesisDotColor={synthesisDotColor}
                          recordId={record.serverRecordId}
                          onReadAloud={!ttsEnabled ? undefined : handleReadAloud}
                          ttsActiveProviderId={
                            ttsPlayer.active && ttsPlayer.active.recordId === record.serverRecordId
                              ? ttsPlayer.active.providerId
                              : null
                          }
                          ttsProgress={ttsProgress}
                          isProviderTTSCached={(pid) => isCardTTSCached(record.serverRecordId, pid)}
                          initialPage={(() => {
                            // S5: prefer the record-keyed page if set. For the
                            // most-recently-appended record (just archived
                            // from active), fall back to "__active__" so the
                            // pager renders at the correct page on its FIRST
                            // mount — no remount, no flash at page 0. The S5
                            // effect below will then transfer "__active__" →
                            // serverRecordId for future mounts.
                            if (!record.serverRecordId) return undefined;
                            const fromRecord = pagerPagesRef.current.get(record.serverRecordId);
                            if (typeof fromRecord === "number") return fromRecord;
                            if (idx === chatHistory.length - 1) {
                              return pagerPagesRef.current.get("__active__");
                            }
                            return undefined;
                          })()}
                          onPageChange={(p) => {
                            if (record.serverRecordId) {
                              pagerPagesRef.current.set(record.serverRecordId, p);
                            }
                          }}
                        />
                      ) : (
                        <ResponseGrid
                          responses={record.responses}
                          providerOrder={record.providerOrder}
                          providers={providers}
                          isStreaming={recordInFlight}
                          mode={record.mode}
                          onRetry={buildRetryHandler(idx)}
                          synthesisCard={isAutoSurface ? judgeCardEl : undefined}
                          onProviderExpand={!isMobile ? (pid) => setExpandedCard({ kind: "history-provider", recordIdx: idx, providerId: pid }) : undefined}
                          recordId={record.serverRecordId}
                          onReadAloud={!ttsEnabled ? undefined : handleReadAloud}
                          ttsActiveProviderId={
                            ttsPlayer.active && ttsPlayer.active.recordId === record.serverRecordId
                              ? ttsPlayer.active.providerId
                              : null
                          }
                          ttsProgress={ttsProgress}
                          isProviderTTSCached={(pid) => isCardTTSCached(record.serverRecordId, pid)}
                        />
                      )}
                    </div>

                    {/* Post-hoc footer — only when NOT auto. */}
                    {!isAutoSurface && (interruptedNotice ?? judgeCardEl)}
                  </>
                );
              })()}
              </>}
            </div>
          ))}

          {/* Pending prompt (shown immediately while streaming) */}
          {showPendingPrompt && pendingPrompt && (
            <div ref={queryAreaRef} className="snap-start" style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {pendingPromptTime.current && (
                <div style={{ fontSize: "12px", color: "var(--text-tertiary)", padding: "0 4px" }}>
                  {pendingPromptTime.current.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at {pendingPromptTime.current.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                </div>
              )}
              <div
                className="prompt-box"
                style={{
                  position: "relative",
                  padding: "10px 38px 10px 14px",
                  background: "var(--surface-hover)",
                  borderRadius: "var(--radius-lg)",
                  color: "var(--text)",
                  fontSize: "14px",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {pendingPrompt}
                <PromptCopyButton text={pendingPrompt} />
              </div>
            </div>
          )}

          {isPreparingDeepResearch && (
            <div
              className="snap-start"
              role="status"
              style={{ display: "flex", alignItems: "center", gap: "8px", color: "var(--text-secondary)", fontSize: "14px" }}
            >
              <div className="spinner" />
              Preparing your research brief…
            </div>
          )}

          {showActiveDeepResearch && activeDeepResearch && (
            <div className="snap-start" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {!isDeepResearchTerminal(activeDeepResearch.status.status) ? (
                <DeepResearchProgress
                  startedAt={activeDeepResearch.startedAt}
                  searches={activeDeepResearchToolCounts.webSearchCount}
                  corpusLookups={activeDeepResearchToolCounts.corpusLookupCount}
                  costUsd={activeDeepResearch.status.cost_usd}
                  runCounts={activeDeepResearch.status.run_counts}
                  expectedDurationSeconds={activeDeepResearch.expectedDurationSeconds || undefined}
                  cancelling={activeDeepResearch.cancelling}
                  onCancel={() => { void handleCancelDeepResearch(); }}
                  lane={activeDeepResearch.status.lane}
                  stages={activeDeepResearch.status.stages}
                  stage={activeDeepResearch.status.stage}
                  stagesDone={activeDeepResearch.status.stages_done}
                  stageStartedAt={activeDeepResearch.status.stage_started_at}
                  detail={activeDeepResearch.status.detail}
                  earlyLook={activeDeepResearch.status.early_look}
                  expectedFinishAt={activeDeepResearch.status.expected_finish_at}
                  attachmentsIgnored={activeDeepResearch.attachmentsIgnored}
                />
              ) : (
                <DeepResearchCard
                  report={activeDeepResearch.status.status === "done" ? activeDeepResearch.status.report : undefined}
                  sources={activeDeepResearch.status.sources}
                  brief={activeDeepResearch.brief}
                  fromFallback={activeDeepResearch.fromFallback}
                  terminalMessage={activeDeepResearch.status.error}
                  status={activeDeepResearch.status.status === "cancelled" || activeDeepResearch.status.status === "failed" || activeDeepResearch.status.status === "error" ? activeDeepResearch.status.status : undefined}
                  metadata={{
                    model: activeDeepResearch.status.model,
                    durationSeconds: (Date.now() - activeDeepResearch.startedAt) / 1_000,
                    costUsd: activeDeepResearch.status.cost_usd,
                    corpusDocumentsRead: activeDeepResearch.status.corpus_documents_read,
                    corpusCitedCount: activeDeepResearch.status.corpus_documents_cited,
                    sourceCount: activeDeepResearch.status.sources?.length,
                  }}
                  onOpenCorpusDocument={setCorpusDocumentSource}
                  recordId={activeDeepResearch.status.status === "done" ? activeDeepResearch.jobId : undefined}
                  ttsProviderId={activeDeepResearch.status.status === "done" ? "deep_research" : undefined}
                  onReadAloud={!ttsEnabled ? undefined : handleReadAloud}
                  isTTSActive={isCardTTSActive(activeDeepResearch.status.status === "done" ? activeDeepResearch.jobId : undefined, "deep_research")}
                  ttsProgress={isCardTTSActive(activeDeepResearch.status.status === "done" ? activeDeepResearch.jobId : undefined, "deep_research") ? ttsProgress : undefined}
                  isTTSCached={isCardTTSCached(activeDeepResearch.status.status === "done" ? activeDeepResearch.jobId : undefined, "deep_research")}
                />
              )}
            </div>
          )}

          {/* Active streaming responses */}
          {hasActiveResponses && (() => {
            // When the user pressed composer Judge, show the synthesis card
            // leftmost from t=0 — providers stream in the remaining 3 slots,
            // synthesis card shows "Waiting for output..." until /judge fires.
            // For brand-new-chat auto-judge, handleSendMessage's onSessionCreated
            // callback adds the new sid to autoJudgeSessions BEFORE streaming
            // begins — so this single check is sufficient and per-session,
            // and a concurrent auto-judge in another session cannot leak into
            // this session's render.
            // iOS canonical: CompactChatView.swift:888-917.
            // Temp chats register under TEMP_JUDGE_KEY in autoJudgeSessions
            // (see handleSendAndJudge's isTempChat branch) so this read
            // surfaces the synthesis-card slot for temp too.
            const autoMapKey = isTempChat ? TEMP_JUDGE_KEY : activeSessionId;
            const showInflightSynthesis =
              !!autoMapKey && autoJudgeSessions.has(autoMapKey);
            const inflightSynthesisCard = showInflightSynthesis ? (
              <JudgeCard
                verdict={null}
                providers={providers}
                isLoading={false}
                isWaitingForOutputs={true}
                scoringProvider={undefined}
                isStreamingContext={true}
                onExpand={!isMobile ? () => setExpandedCard({ kind: "active-synthesis" }) : undefined}
              />
            ) : undefined;

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {isMobile ? (
                  <ResponsePager
                    responses={activeResponses}
                    providerOrder={activeProviderOrder}
                    providers={providers}
                    isStreaming={isStreaming}
                    mode={selectedMode}
                    onRetry={buildRetryHandler(chatHistory.length)}
                    synthesisCard={inflightSynthesisCard}
                    initialPage={pagerPagesRef.current.get("__active__")}
                    onPageChange={(p) => {
                      pagerPagesRef.current.set("__active__", p);
                    }}
                    // Tests 1.2 + 1.11: pass the client-minted activeRecordId
                    // so the per-card play button can render and fire DURING
                    // streaming, not only after archive. Audio path uses a
                    // text-snapshot at click time via /tts/stream-text.
                    recordId={activeRecordId ?? undefined}
                    onReadAloud={!ttsEnabled ? undefined : handleReadAloud}
                    ttsActiveProviderId={
                      ttsPlayer.active && ttsPlayer.active.recordId === activeRecordId
                        ? ttsPlayer.active.providerId
                        : null
                    }
                    ttsProgress={ttsProgress}
                    isProviderTTSCached={(pid) => isCardTTSCached(activeRecordId ?? undefined, pid)}
                  />
                ) : (
                  <ResponseGrid
                    responses={activeResponses}
                    providerOrder={activeProviderOrder}
                    providers={providers}
                    isStreaming={isStreaming}
                    mode={selectedMode}
                    onRetry={buildRetryHandler(chatHistory.length)}
                    synthesisCard={inflightSynthesisCard}
                    onProviderExpand={!isMobile ? (pid) => setExpandedCard({ kind: "active-provider", providerId: pid }) : undefined}
                    recordId={activeRecordId ?? undefined}
                    onReadAloud={!ttsEnabled ? undefined : handleReadAloud}
                    ttsActiveProviderId={
                      ttsPlayer.active && ttsPlayer.active.recordId === activeRecordId
                        ? ttsPlayer.active.providerId
                        : null
                    }
                    ttsProgress={ttsProgress}
                    isProviderTTSCached={(pid) => isCardTTSCached(activeRecordId ?? undefined, pid)}
                  />
                )}
              </div>
            );
          })()}
          {activeCustomQueryName && (
            <div style={{
              display: "flex",
              justifyContent: "center",
              padding: "4px 0",
            }}>
              <span style={{
                padding: "4px 14px",
                fontSize: "13px",
                fontWeight: 500,
                color: "var(--accent)",
                background: "var(--bg)",
                border: "1px solid var(--accent)",
                borderRadius: "16px",
                lineHeight: "1.4",
              }}>
                {activeCustomQueryName}
              </span>
            </div>
          )}
        </div>

        {/* Prompt composer area */}
        <div className="prompt-composer-area" ref={bottomBarRef}>
          {sentAttachments.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", padding: "4px 12px" }}>
              {sentAttachments.map((att, i) => (
                <span
                  key={i}
                  onClick={att.id ? async () => {
                    try {
                      const resp = await fetch(`${process.env.NEXT_PUBLIC_API_BASE || ""}/attachments/${att.id}`, {
                        headers: getAuthHeaders(),
                      });
                      if (!resp.ok) return;
                      const blob = await resp.blob();
                      window.open(URL.createObjectURL(blob), "_blank");
                    } catch { /* ignore */ }
                  } : undefined}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    fontSize: "12px", fontWeight: 500,
                    color: "var(--accent)",
                    background: "var(--accent-muted)",
                    border: "1px solid color-mix(in srgb, var(--accent) 50%, transparent)",
                    borderRadius: "999px", padding: "4px 10px",
                    cursor: att.id ? "pointer" : "default",
                  }}
                  title={att.id ? "Click to view" : att.name}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  {att.name}
                </span>
              ))}
            </div>
          )}
          {/* Folder/sharing indicator — sits ABOVE the textarea on both
              mobile and desktop (was previously inline in BottomToolbar's
              center spacer; mobile collapsed it to 0 width with 7 buttons
              + nowrap, and the user wanted it above the input regardless
              of platform). */}
          {(activeFolderName || activeSharedBy || (activeSharedWith && activeSharedWith.length > 0)) && (
            <div style={{ display: "flex", justifyContent: "center", padding: "0 4px 4px", overflow: "hidden" }}>
              <span style={{ fontSize: "11px", color: "var(--accent)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                {activeFolderName && <span>📁 {activeFolderName}</span>}
                {activeFolderName && <span> · </span>}
                {activeFolderName && (
                  <span
                    onClick={handleFolderContextClick}
                    style={{ cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }}
                  >
                    Memory{activeFolderContextDate ? `: ${activeFolderContextDate}` : ""}
                  </span>
                )}
                {activeFolderName && (activeSharedBy || (activeSharedWith && activeSharedWith.length > 0)) && <span> · </span>}
                {activeSharedBy && <span>Shared by {activeSharedBy}</span>}
                {activeSharedWith && activeSharedWith.length > 0 && <span>Shared with {activeSharedWith.join(", ")}</span>}
              </span>
            </div>
          )}
          <PromptComposer
            onSend={handleSendMessage}
            onSendAndJudge={autoJudgeEnabled ? handleSendAndJudge : undefined}
            judgeByDefault={autoJudgeEnabled && selectedMode !== "deep_research" && composerEffectiveProviders.length >= 2}
            onSendFavorite={handleSendFavorite}
            singleProviderAbbrev={singleProviderAbbrev}
            singleProviderColor={singleProviderColor}
            prefillRequest={composerPrefill}
            onStop={stopStreaming}
            onDiscard={() => {
              // Three possible states at click time:
              //   (1) snap is set — addRecord landed → server-side cascade
              //       handles record + (if last) session in one atomic call.
              //   (2) snap is null AND chatHistory is empty AND we have an
              //       activeSessionId — this is the "fast-Stop edge case":
              //       the session was committed server-side at send time
              //       but addRecord never fired (user clicked Stop before
              //       finalize could save). The session is empty server-
              //       side; cascade can't fire because there's nothing to
              //       delete. We must directly DELETE the session ourselves
              //       so it doesn't ghost in the sidebar.
              //   (3) snap is null AND chatHistory has records — multi-turn
              //       case where the user discarded a follow-up before
              //       addRecord fired. Queue via discardRecentlyStopped so
              //       the drain useEffect handles it when (if) addRecord
              //       eventually lands the recordId. Do NOT delete the
              //       session — it has valuable prior turns.
              const snap = recentlyStoppedRecord;
              const sidAtClickTime = activeSessionId;
              const isEmptyFreshSession =
                !snap && !!sidAtClickTime && chatHistory.length === 0;
              handleNewChat();
              if (snap) {
                void (async () => {
                  try {
                    const result = await deleteRecord(snap.sessionId, snap.recordId);
                    if (result.session_deleted) {
                      try {
                        await deleteSession(snap.sessionId);
                      } catch (err) {
                        console.error("Failed to mirror cascade delete locally:", err);
                      }
                      // Force-refresh from server truth so the sidebar
                      // updates within ~100-200ms instead of waiting for
                      // the next 5s poll cycle.
                      void loadSessions();
                    }
                  } catch (err) {
                    console.error("Failed to discard:", err);
                  }
                })();
              } else if (isEmptyFreshSession) {
                // Fast-Stop edge case — empty session server-side. Direct delete.
                void (async () => {
                  try {
                    await deleteSession(sidAtClickTime);
                    void loadSessions();
                  } catch (err) {
                    console.error("Failed to delete empty session on discard:", err);
                  }
                })();
              } else {
                void discardRecentlyStopped();
              }
            }}
            showDiscardInsteadOfSend={
              // Show Discard either way: synchronously after Stop press
              // (stopJustPressed) for instant feedback, OR once finalize
              // lands the saved record id (recentlyStoppedRecord). The two
              // cover the visible-Discard window together.
              stopJustPressed ||
              (!!recentlyStoppedRecord &&
              recentlyStoppedRecord.sessionId === activeSessionId)
            }
            isStreaming={isStreaming}
            disabled={false}
            onFilesSelected={handleFilesSelected}
            pendingAttachments={pendingAttachments}
            onRemoveAttachment={handleRemoveAttachment}
            suggestedPrompt={suggestedPrompt}
            onAcceptSuggestion={() => setSuggestedPrompt(null)}
          />
          <BottomToolbar
            providers={providers}
            modes={modes}
            selectedProviders={selectedProviders}
            selectedMode={selectedMode}
            isStreaming={isStreaming}
            isTempChat={isTempChat}
            isMobile={isMobile}
            folderName={activeFolderName}
            folderContextDate={activeFolderContextDate}
            sharedWith={activeSharedWith}
            sharedBy={activeSharedBy}
            onToggleProvider={toggleProvider}
            onReorderProviders={(order) => setSelectedProviders(order)}
            onModeChange={(mode) => {
              setSelectedMode(mode);
              if (mode === "deep_research" && pendingAttachments.length > 0) {
                showToast("Attachments are not used in Deep Research.", "info");
              }
            }}
            onToggleTempChat={() => {
              if (!isTempChat) {
                // Turning on temp chat — remember current folder, then start fresh
                setTempChatFolderId(activeSession?.folder_id || null);
                setActiveSession(null);
                clearChat();
                setPendingPrompt(null);
                clearCustomQueryState();
              } else {
                // Turning off — drop in-memory temp history so leaving temp
                // doesn't leak prior temp turns into the regular new-chat view.
                setTempChatFolderId(null);
                clearChat();
                setPendingPrompt(null);
              }
              setIsTempChat(!isTempChat);
            }}
            onFilesSelected={handleFilesSelected}
            onCustomQuery={(queryId) => {
              if (queryId) {
                const q = customQueries.find((cq) => cq.id === queryId);
                if (q) {
                  setActiveSession(null);
                  clearChat();
                  setPendingPrompt(null);
                  setActiveCustomQueryName(q.name);
                  setActiveCustomQueryPrompt(q.system_prompt || null);
                  setSuggestedPrompt(q.suggested_prompt || null);
                  if (q.default_mode) setSelectedMode(q.default_mode);
                  if (q.folder_id) setNewChatFolderId(q.folder_id);
                }
              }
            }}
            onAddCustomQuery={() => { setShowSettings(true); setSettingsInitialTab("queries"); }}
            onManageCustomQueries={() => { setShowSettings(true); setSettingsInitialTab("queries"); }}
            onFolderContextClick={handleFolderContextClick}
            customQueries={customQueries}
            folders={folders}
            onSelectFolder={handleNewChatInFolder}
            onNewChat={handleNewChat}
          />
        </div>
      </div>

      {/* Floating TTS transport bar — fixed above the measured composer via
          --bottom-bar-h, so it is available without overlapping mobile input. */}
      {ttsEnabled && <TTSTransportBar player={ttsPlayer} />}

      {/* Mobile History Sheet */}
      {isMobile && (
        <HistorySheet
          {...sidebarProps}
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
        />
      )}

      {/* Desktop-web tap-to-expand popup */}
      {expandedCard && !isMobile && (() => {
        // Look up the prompt + render the matching card from current state.
        // Read-from-state-each-render means the popup stays live as the
        // underlying card streams (e.g. text appending during a chat or a
        // judge run completing).
        let promptText = "";
        let promptTimestamp: Date | null = null;
        let cardNode: React.ReactNode = null;

        if (expandedCard.kind === "active-provider") {
          promptText = pendingPrompt ?? "";
          promptTimestamp = pendingPromptTime.current;
          const resp = activeResponses[expandedCard.providerId];
          const prov = providers.find((p) => p.id === expandedCard.providerId);
          if (resp && prov) {
            const derived = prov.models_by_mode && resp.model
              ? Object.entries(prov.models_by_mode).find(([, m]) => m.model === resp.model)?.[0]
              : undefined;
            const cardMode = resp.mode || (selectedMode === "auto" ? derived : selectedMode) || derived;
            cardNode = (
              <ResponseCard
                providerId={expandedCard.providerId}
                providerName={prov.display_name}
                providerColor={prov.color}
                providerIcon={prov.icon}
                response={resp}
                isStreaming={isStreaming && !resp.isComplete}
                mode={cardMode}
                inPopup
              />
            );
          }
        } else if (expandedCard.kind === "active-synthesis") {
          promptText = pendingPrompt ?? "";
          promptTimestamp = pendingPromptTime.current;
          cardNode = (
            <JudgeCard
              verdict={null}
              providers={providers}
              isLoading={false}
              isWaitingForOutputs={true}
              isStreamingContext={true}
              inPopup
            />
          );
        } else if (expandedCard.kind === "history-provider") {
          const record = chatHistory[expandedCard.recordIdx];
          if (record) {
            promptText = record.prompt;
            promptTimestamp = record.timestamp;
            const resp = record.responses[expandedCard.providerId];
            const prov = providers.find((p) => p.id === expandedCard.providerId);
            if (resp && prov) {
              const derived = prov.models_by_mode && resp.model
                ? Object.entries(prov.models_by_mode).find(([, m]) => m.model === resp.model)?.[0]
                : undefined;
              const cardMode = resp.mode || (record.mode === "auto" ? derived : record.mode) || derived;
              cardNode = (
                <ResponseCard
                  providerId={expandedCard.providerId}
                  providerName={prov.display_name}
                  providerColor={prov.color}
                  providerIcon={prov.icon}
                  response={resp}
                  isStreaming={false}
                  mode={cardMode}
                  inPopup
                  recordId={record.serverRecordId}
                  onReadAloud={!ttsEnabled ? undefined : handleReadAloud}
                  isTTSActive={isCardTTSActive(record.serverRecordId, expandedCard.providerId)}
                  ttsProgress={
                    isCardTTSActive(record.serverRecordId, expandedCard.providerId)
                      ? ttsProgress
                      : undefined
                  }
                  isTTSCached={isCardTTSCached(record.serverRecordId, expandedCard.providerId)}
                />
              );
            }
          }
        } else if (expandedCard.kind === "history-judge") {
          const record = chatHistory[expandedCard.recordIdx];
          if (record) {
            promptText = record.prompt;
            promptTimestamp = record.timestamp;
            cardNode = (
              <JudgeCard
                verdict={record.judgeVerdict || null}
                providers={providers}
                promptHash={judgePromptHashes.get(record.prompt)}
                isStatsExcluded={judgeStatsExcludedHashes.has(judgePromptHashes.get(record.prompt) ?? "")}
                onSetStatsExcluded={(promptHash, excluded) =>
                  setPromptStatsExcluded(record.prompt, promptHash, excluded)
                }
                isLoading={false}
                inPopup
                recordId={record.serverRecordId}
                onReadAloud={!ttsEnabled ? undefined : handleReadAloud}
                isTTSActive={isCardTTSActive(record.serverRecordId, record.judgeVerdict ? judgeSpeechCardId(record.judgeVerdict) : "judge")}
                ttsProgress={
                  isCardTTSActive(record.serverRecordId, record.judgeVerdict ? judgeSpeechCardId(record.judgeVerdict) : "judge") ? ttsProgress : undefined
                }
                isTTSCached={isCardTTSCached(record.serverRecordId, record.judgeVerdict ? judgeSpeechCardId(record.judgeVerdict) : "judge")}
              />
            );
          }
        }

        if (!cardNode) {
          // Underlying data went away (record deleted, active cleared) —
          // dismiss silently rather than render a blank box.
          return null;
        }

        return (
          <CardExpandPopup
            prompt={promptText}
            promptTime={promptTimestamp}
            onClose={() => setExpandedCard(null)}
          >
            {cardNode}
          </CardExpandPopup>
        );
      })()}

      {/* Settings Modal */}
      <SettingsPanel
        isOpen={showSettings}
        onClose={() => {
          setShowSettings(false);
          setSettingsInitialTab(null);
          // SettingsPanel has its own usePreferences() instance — when it
          // saves, only ITS local state updates. page.tsx's separate
          // instance is stale until reloaded. Without this, parameter
          // overrides (e.g. Anthropic thinking_budget = "low") set in
          // Settings are silently dropped on the next chat send because
          // buildProviderRequestFor reads page.tsx's stale prefs.
          void loadPreferences();
        }}
        initialTab={settingsInitialTab}
        onCustomQueriesChanged={refreshCustomQueries}
      />

      {/* Folder Memory Modal */}
      <Modal
        isOpen={folderContextModal !== null}
        onClose={() => setFolderContextModal(null)}
        title={folderContextModal?.title || "Folder"}
      >
        <div style={{ fontSize: "14px", lineHeight: 1.6, color: "var(--text-primary)", maxHeight: "60vh", overflowY: "auto" }}>
          <p style={{ marginTop: 0, color: "var(--text-secondary)" }}>
            What Trio remembers about your situation in this folder. Delete anything you don't want it to use.
          </p>
          {folderContextModal?.items.length === 0 ? (
            <p style={{ color: "var(--text-secondary)" }}>
              Nothing remembered yet. Trio builds this automatically from your chats in this folder.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {folderContextModal?.items.map((item) => (
                <div key={item.id} style={{ display: "flex", gap: "10px", borderBottom: "1px solid var(--border)", paddingBottom: "10px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: "8px", color: "var(--text-secondary)", fontSize: "12px", marginBottom: "3px" }}>
                      <span style={{ fontWeight: 600 }}>{folderMemoryTypeLabel(item.type)}</span>
                      <span>{item.date}</span>
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", userSelect: "text" }}>{item.text}</div>
                  </div>
                  <button
                    type="button"
                    aria-label="Delete memory item"
                    onClick={() => void handleDeleteFolderMemoryItem(item.id)}
                    style={{ alignSelf: "flex-start", border: 0, background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: "18px" }}
                  >
                    🗑
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Move to Folder Picker */}
      <Modal
        isOpen={moveSessionId !== null}
        onClose={() => setMoveSessionId(null)}
        title="Move to Folder"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <button
            className="btn-ghost"
            style={{ justifyContent: "flex-start", padding: "8px 12px", fontWeight: 600, color: "var(--accent, var(--text))" }}
            onClick={() => {
              const sid = moveSessionId;
              setMoveSessionId(null);
              setNewFolderForSession(sid);
            }}
          >
            + New Folder…
          </button>
          <div style={{ height: "1px", background: "var(--border)", margin: "2px 0" }} />
          <button
            className="btn-ghost"
            style={{ justifyContent: "flex-start", padding: "8px 12px" }}
            onClick={async () => {
              if (moveSessionId) {
                // Capture the source folder BEFORE the update so we can
                // regen its context — once a member leaves, the folder's
                // existing context is stale. Mirrors iOS moveSession at
                // SessionsViewModel.swift:1951.
                const priorFolderId = sessions.find(s => s.id === moveSessionId)?.folder_id || null;
                // Batch "move" preserves updated_at (no jump to top of Recent).
                await batchSessions({ action: "move", session_ids: [moveSessionId], folder_id: "" });
                loadSessions();
                if (priorFolderId) {
                  void generateFolderContext(priorFolderId).catch(() => {});
                }
                setMoveSessionId(null);
              }
            }}
          >
            Unfiled
          </button>
          {[...folders].sort(compareFolders).map((folder) => (
            <Fragment key={folder.id}>
            {folder.separator_before && <div style={{ height: "2px", background: "#484848", borderRadius: "1px", margin: "4px 8px" }} />}
            <button
              className="btn-ghost"
              style={{ justifyContent: "flex-start", padding: "8px 12px", width: "100%" }}
              onClick={async () => {
                if (moveSessionId) {
                  const priorFolderId = sessions.find(s => s.id === moveSessionId)?.folder_id || null;
                  // Batch "move" preserves updated_at (no jump to top of Recent).
                  await batchSessions({ action: "move", session_ids: [moveSessionId], folder_id: folder.id });
                  loadSessions();
                  // Regen destination + source (if it lost an item).
                  // Fire-and-forget; server runs the summarization async
                  // and the next /folders/{id}/context fetch picks it up.
                  void generateFolderContext(folder.id).catch(() => {});
                  if (priorFolderId && priorFolderId !== folder.id) {
                    void generateFolderContext(priorFolderId).catch(() => {});
                  }
                  setMoveSessionId(null);
                }
              }}
            >
              {folder.name}
            </button>
            </Fragment>
          ))}
        </div>
      </Modal>

      {/* Single-session "Move → New Folder…" name dialog */}
      <NewFolderDialog
        isOpen={newFolderForSession !== null}
        onClose={() => setNewFolderForSession(null)}
        onCreate={async (name) => {
          if (newFolderForSession) {
            await handleBulkMoveToNewFolder([newFolderForSession], name);
          }
          setNewFolderForSession(null);
        }}
      />

      {/* Delete confirm dialogs removed — undo-delete toast replaces them */}

      {/* Friend Manager Modal */}
      <FriendManager
        isOpen={showFriendManager}
        onClose={() => {
          setShowFriendManager(false);
          fetchFriends()
            .then((data) => setPendingFriendCount(data.pending_received.length))
            .catch((err) => console.error("Failed to refresh friends on close:", err));
        }}
      />

      {/* Combined Share Panel */}
      <SharePanel
        isOpen={showShareMenu}
        onClose={() => {
          setShowShareMenu(false);
          fetchSharedInbox()
            .then((data) => setUnseenSharedCount(data.total_unseen))
            .catch((err) => console.error("Failed to refresh shared inbox on close:", err));
        }}
        sessionId={activeSessionId && !activeSession?.shared_by ? activeSessionId : undefined}
        ownedSharedSessions={sessions
          .filter((s) => s.shared && s.owner_email === email && (s.participants?.length ?? 0) > 0)
          .map((s) => ({
            session_id: s.id,
            title: s.title,
            participants: s.participants ?? [],
          }))}
        onShared={() => {
          loadSessions();
          showToast("Chat shared successfully", "success");
        }}
        onSharedItemClick={(sessionId) => {
          setActiveSession(sessionId);
          fetchSharedInbox()
            .then((data) => setUnseenSharedCount(data.total_unseen))
            .catch((err) => console.error("Failed to refresh shared inbox after item click:", err));
        }}
        onManageSharing={(sessionId) => {
          setActiveSession(sessionId);
          setShowManageSharing(true);
        }}
        latestRecord={chatHistory.length > 0 ? chatHistory[chatHistory.length - 1] : null}
        providers={providers}
        isTempChat={isTempChat}
      />

      {/* Manage Sharing Modal */}
      {activeSessionId && activeSession?.participants && (
        <ManageSharing
          isOpen={showManageSharing}
          onClose={() => {
            setShowManageSharing(false);
            loadSessions();
          }}
          sessionId={activeSessionId}
          participants={activeSession.participants as unknown as string[]}
        />
      )}

      {/* Help Modal */}
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} isMobile={isMobile} />

      {/* Toast */}
      <Toast
        message={toast?.message || ""}
        type={toast?.type}
        isVisible={toast !== null}
        onDismiss={() => setToast(null)}
        action={toast?.action}
        duration={toast?.action ? 5000 : undefined}
      />
      {/* Soft-delete undo banner — peer of Toast so the z-stack is the
          same. Drives off recordPendingDelete state; auto-disappears
          when commitPendingRecordDelete runs (5s timer / send /
          tab hidden). */}
      {recordPendingDelete && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 16px",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            fontSize: 14,
            color: "var(--text)",
            maxWidth: 480,
            minWidth: 280,
          }}
        >
          <span style={{ fontSize: 18 }}>🗑</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 500 }}>Query deleted</span>
            <span
              style={{
                fontSize: 12,
                color: "var(--text-secondary)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {recordPendingDelete.promptPreview}
            </span>
          </div>
          <button
            type="button"
            onClick={undoPendingRecordDelete}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--accent)",
              fontWeight: 600,
              cursor: "pointer",
              padding: "4px 8px",
            }}
          >
            Undo
          </button>
        </div>
      )}
      {/* Right-click context menu for prompt bubbles. Fixed-position
          floating menu anchored at the click coords. Click-outside
          handler in the useEffect above closes it. */}
      {promptContextMenu && (
        <div
          style={{
            position: "fixed",
            top: promptContextMenu.y,
            left: promptContextMenu.x,
            zIndex: 1200,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
            padding: "4px",
            minWidth: 160,
          }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            type="button"
            onClick={() => {
              const { sessionId, recordId } = promptContextMenu;
              setPromptContextMenu(null);
              beginRecordDelete(sessionId, recordId);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "8px 12px",
              background: "transparent",
              border: "none",
              color: "var(--danger, #d44)",
              cursor: "pointer",
              fontSize: 14,
              borderRadius: "var(--radius-sm)",
              textAlign: "left",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "var(--surface-hover)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "transparent";
            }}
          >
            <span>🗑</span>
            <span>Delete query</span>
          </button>
        </div>
      )}
      {/* Memory toast — shows for 5 seconds with brain icon + Cancel button
          that deletes the just-created memories before they stick. */}
      <Toast
        message={memoryToast ? `🧠 ${memoryToast.message}` : ""}
        type="info"
        isVisible={memoryToast !== null}
        onDismiss={() => setMemoryToast(null)}
        duration={5000}
        action={memoryToast ? {
          label: "Cancel",
          onClick: () => {
            const ids = memoryToast.ids;
            setMemoryToast(null);
            // Fire-and-forget deletes — best effort, no UI blocking. If any
            // fail, the memory persists; user can still delete it via
            // Settings → Memories later.
            for (const id of ids) {
              deleteMemory(id).catch(() => {});
            }
          },
        } : undefined}
      />
      {deepResearchDocument && (
        <DeepResearchDocument
          report={deepResearchDocument.report}
          sources={deepResearchDocument.sources}
          brief={deepResearchDocument.brief}
          fromFallback={deepResearchDocument.fromFallback}
          onClose={() => setDeepResearchDocument(null)}
          onOpenCorpusDocument={setCorpusDocumentSource}
        />
      )}
      {corpusDocumentSource && <CorpusDocumentViewer source={corpusDocumentSource} onClose={() => setCorpusDocumentSource(null)} />}
    </div>
    </div>
  );
}
